/* Live chat view — backed by /api/chat (Claude proxy + D1 persistence). */
import React from 'react';
import { Avatar, Icon, MimeIcon, ThumbsRow } from './primitives.jsx';
import { MarkdownView } from './orgrepo-news-reports-arch.jsx';
import { HumanAnswerForm, AgentRunInline } from './agent-run-board.jsx';
import {
  cancelSpeech, sttSupported, ttsSupported, speakCloud,
  prefetchTtsPayload, playTtsPayload,
  mediaRecorderSupported, startWhisperRecording,
  acquireMicStream, releaseMicStream,
  playOnTone, playOffTone,
} from '../lib/voice.js';
import { RealtimeClient } from '../lib/realtime.js';
import {
  streamChat, getChatMessages, postChatMessage,
  CLAUDE_MODELS, DEFAULT_MODEL, DESIGN_SYSTEMS,
  uploadChatFile, deleteChatFile,
  evaluateChatReply,
  listSkills,
} from '../api.js';

const SPEAK_LS_KEY = 'erasphere.chatSpeakReplies';

const MODEL_LS_KEY    = 'erasphere.chatModel';
const DESIGN_LS_KEY   = 'erasphere.chatDesignSystem';
const SELFLEARN_LS_KEY = 'erasphere.selfLearning';
const SKILLS_LS_KEY    = 'erasphere.chatSkillIds';

/* Pick the best-sounding speechSynthesis voice the OS exposes.
 *
 * Priorities (in order):
 *   1. Exact match on user language + a high-quality name tag
 *      (Siri / Neural / Premium / Enhanced / Natural / Google …).
 *   2. Any voice for the user language tagged as not "default" but
 *      with one of the quality tags above.
 *   3. Best non-tagged voice for the user language (prefer "local"
 *      voices on iOS — they're the Siri-quality ones).
 *   4. First voice for the user language.
 *   5. Fall back to whatever the browser uses by default (null).
 *
 * iOS Safari ships Siri 1/2/3/4 for many languages — those carry
 * "(Premium)" or named tags ("Ava", "Samantha") that we surface.
 * Chrome desktop ships Google US English etc. */
const VOICE_QUALITY_TAGS = ['siri', 'neural', 'premium', 'enhanced', 'natural', 'google', 'samantha', 'ava', 'allison', 'tom', 'fred', 'nicky', 'karen', 'daniel'];
export function pickBestVoice(voices) {
  if (!Array.isArray(voices) || voices.length === 0) return null;
  const lang = ((typeof navigator !== 'undefined' && navigator.language) || 'en-US').toLowerCase();
  const langShort = lang.split('-')[0];
  const sameLang = voices.filter(v => (v.lang || '').toLowerCase().startsWith(langShort));
  const candidates = sameLang.length > 0 ? sameLang : voices;
  const tag = (v) => (v.name + ' ' + (v.voiceURI || '')).toLowerCase();
  // Pass 1: tag match + exact lang.
  const exactLang = candidates.filter(v => (v.lang || '').toLowerCase() === lang);
  for (const v of exactLang) {
    if (VOICE_QUALITY_TAGS.some(t => tag(v).includes(t))) return v;
  }
  // Pass 2: tag match + same lang family.
  for (const v of candidates) {
    if (VOICE_QUALITY_TAGS.some(t => tag(v).includes(t))) return v;
  }
  // Pass 3: prefer locally-installed voices (iOS Siri voices are
  // localService=true; cloud-only voices like "Samantha (Cloud)"
  // sometimes lag).
  const local = candidates.filter(v => v.localService);
  if (local.length > 0) return local[0];
  return candidates[0] || null;
}

export const LiveChatView = ({ user, chatId, onChatCreated, onChatTouched, onAttachmentsChange, onOpenList }) => {
  const [messages, setMessages] = React.useState([]);
  const [composer, setComposer] = React.useState('');
  const [composerExpanded, setComposerExpanded] = React.useState(false);
  // (Old separate conversationMode overlay is gone — voice is handled
  // inline on the chat composer now via the 3-state cycle below.)
  // Voice mode is a 3-state cycle on the bottom mic button:
  //   'off'          — no dictation, plain typing only
  //   'dictate'      — voice transcribes into the composer; user
  //                    edits + taps Send manually (no auto-send)
  //   'conversation' — voice transcribes into the composer AND
  //                    auto-sends on 2.5 s of silence; assistant
  //                    replies are spoken back as a brief summary
  // Click cycles off → dictate → conversation → off.
  const [voiceMode, setVoiceMode] = React.useState('off');
  const composerRef = React.useRef(null);
  const [streaming, setStreaming] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const scrollRef = React.useRef(null);
  const abortRef = React.useRef(null);
  // Track chats this component just created so the `chatId` change
  // doesn't trigger a D1 refetch that races the server-side persist of
  // the trailing assistant message — which used to wipe the answer
  // right after the stream finished.
  const skipReloadRef = React.useRef(null);

  // Files attached to the in-flight composer (before send). Once
  // sent they live on the user message row and render from message.attachments.
  const [pendingAttachments, setPendingAttachments] = React.useState([]);
  const [uploading, setUploading] = React.useState(0);
  const [openAttachment, setOpenAttachment] = React.useState(null);  // { att, messageId? }
  const fileInputRef = React.useRef(null);

  // Sticky per-user preferences, persisted in localStorage. The backend
  // validates anyway — a fat-fingered value just falls back to the default.
  const [model, setModel] = React.useState(() => {
    try { return localStorage.getItem(MODEL_LS_KEY) || DEFAULT_MODEL; } catch { return DEFAULT_MODEL; }
  });
  const [designSystem, setDesignSystem] = React.useState(() => {
    try { return localStorage.getItem(DESIGN_LS_KEY) || DESIGN_SYSTEMS[0].id; } catch { return DESIGN_SYSTEMS[0].id; }
  });
  React.useEffect(() => { try { localStorage.setItem(MODEL_LS_KEY,    model);                 } catch {} }, [model]);
  React.useEffect(() => { try { localStorage.setItem(DESIGN_LS_KEY,   designSystem);          } catch {} }, [designSystem]);

  // Skill multi-select: the user can opt into a set of native skills
  // that get exposed as Anthropic tools on every turn (e.g. people-search,
  // project-search). Empty array = backend keeps its defaults (the chart
  // / table catalog). Persisted to localStorage so the selection sticks.
  const [skillCatalog, setSkillCatalog] = React.useState([]);
  const [selectedSkillIds, setSelectedSkillIds] = React.useState(() => {
    try {
      const raw = localStorage.getItem(SKILLS_LS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(x => typeof x === 'string') : [];
    } catch { return []; }
  });
  const [skillsOpen, setSkillsOpen] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    listSkills()
      .then(list => { if (!cancelled) setSkillCatalog(Array.isArray(list) ? list : []); })
      .catch(() => { /* leave empty — the dropdown will just show nothing */ });
    return () => { cancelled = true; };
  }, []);
  React.useEffect(() => {
    try { localStorage.setItem(SKILLS_LS_KEY, JSON.stringify(selectedSkillIds)); } catch {}
  }, [selectedSkillIds]);

  // Voice: dictation is the only chat-side voice surface. "Read aloud
  // toggle" + "self-learning toggle" used to live here; both are gone.
  //   - read aloud → swallowed into ConversationMode, which owns its
  //     own TTS lifecycle.
  //   - self-learning → admin-only (the system agent
  //     `self-learning-chat-eval` is the source of truth).
  // STT only fires if the browser exposes a SpeechRecognition impl
  // (Chrome / Safari iOS / Edge); Firefox shows the mic button disabled.

  // Dictation: mirror the live transcript into the composer; when the
  // user taps the mic again to stop, auto-send the final transcript so
  // the flow feels like a conversation. `sendRef` papers over the
  // closure-over-stale-state problem — the dictation hook outlives any
  // single render of `send`.
  const sendRef = React.useRef(null);

  // Auto-resize the composer textarea up to ~10 lines as the user
  // types. Above that the textarea scrolls internally. Resets when
  // composer is cleared (on send or via the dictation flow).
  React.useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    if (composerExpanded) {
      // Expanded mode lets the textarea flex to fill the composer.
      // Clear any inline height the auto-resize wrote so flex wins.
      el.style.height = '';
      return;
    }
    el.style.height = 'auto';
    const maxH = 240; // ~10 lines at 15px / line-height 1.5
    el.style.height = Math.min(el.scrollHeight, maxH) + 'px';
  }, [composer, composerExpanded]);

  // Lift the composer above whatever bottom chrome is showing — the
  // on-screen keyboard AND/OR the browser's own bottom toolbar (e.g.
  // iOS Safari's bottom address bar). The body is pinned (position:
  // fixed) and <html> is sized to 100lvh, so html.clientHeight is the
  // LARGE, full-device layout viewport — the same containing block the
  // fixed composer anchors to. visualViewport tracks the VISUAL
  // viewport; the gap between its bottom edge (offsetTop + height) and
  // the layout-viewport bottom is exactly that bottom chrome. We publish
  // it as --kb-inset and the composer rides up by it (see .chat-composer
  // in styles.css). 0 on desktop / when nothing covers the bottom.
  React.useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    const apply = () => {
      const layoutH = root.clientHeight || window.innerHeight;
      const inset = layoutH - vv.height - vv.offsetTop;
      // Ignore sub-pixel rounding noise; anything real (a bottom address
      // bar is tens of px, a soft keyboard hundreds) lifts the composer.
      root.style.setProperty('--kb-inset', inset > 8 ? `${Math.round(inset)}px` : '0px');
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      root.style.removeProperty('--kb-inset');
    };
  }, []);

  // Esc to exit composer-expanded mode (mirrors the fullscreen-chat
  // affordance the user already knows).
  React.useEffect(() => {
    if (!composerExpanded) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setComposerExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [composerExpanded]);

  // ── Voice controller ─────────────────────────────────────────
  // Direct SpeechRecognition (no hook) so we own the lifecycle and
  // don't get caught by React closure issues. Manages two modes:
  //
  //   - 'dictate': transcript appends to the composer; user sends
  //     manually. No silence timer.
  //   - 'conversation': transcript appends to the composer AND the
  //     silence timer auto-sends after 2.5 s. After each assistant
  //     reply, a short voice summary is spoken.
  const voiceModeRef    = React.useRef('off');
  const recRef          = React.useRef(null);
  const finalTextRef    = React.useRef('');
  const composerPrefixRef = React.useRef('');
  const silenceTimerRef = React.useRef(null);
  const lastSpokenIdxRef = React.useRef(-1);
  // True while the entry greeting ("I'm listening") is in flight.
  // The voiceMode effect skips its auto-startRec while this is set
  // so the recognizer doesn't try to open the mic during TTS
  // playback (iOS AVAudioSession can't be in playback + record
  // simultaneously) and so the mic doesn't capture the greeting
  // itself as a phantom transcript.
  const greetingActiveRef = React.useRef(false);
  // True after the FIRST conversation activation in this page
  // session. Subsequent activations skip the spoken greeting — the
  // user already knows the gesture works, and the silence + pulse
  // animation is enough feedback. Less robotic on quick toggles.
  const greetedThisSessionRef = React.useRef(false);

  // Auto-off timer — when the mic is open and we haven't received any
  // real speech (interim or final) for this many ms, we flip voiceMode
  // back to 'off' so the button reverts to the gray no-mic state. iOS
  // SpeechRecognition tends to silently restart on no-speech errors and
  // hold the mic indefinitely otherwise; this timer enforces a clean
  // session end. The user reschedules it just by talking.
  const NO_SPEECH_AUTO_OFF_MS = 10_000;
  const noSpeechTimerRef = React.useRef(null);
  // (Voice errors used to surface as a small chip above the mic button.
  // Removed per user preference — the button flipping back to its gray
  // off-state is signal enough; popups felt noisy.)
  // Animated placeholder string. Mutates char-by-char when entering
  // conversation mode (typewriter effect from "Ask me anything" →
  // "I'm listening").
  const [animPlaceholder, setAnimPlaceholder] = React.useState('Ask me anything');

  React.useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);

  // Rotating acknowledgements so consecutive turns don't say the
  // same thing. Conversational, brief, varied — what a person
  // would say when receiving a request mid-conversation.
  const ACK_PHRASES = React.useRef([
    'OK, on it.',
    'Got it — looking now.',
    'One moment.',
    'Sure, working on that.',
    'OK, let me check.',
  ]).current;
  const ackIdxRef = React.useRef(0);
  const pickAck = React.useCallback(() => {
    const phrase = ACK_PHRASES[ackIdxRef.current % ACK_PHRASES.length];
    ackIdxRef.current++;
    return phrase;
  }, [ACK_PHRASES]);

  // Resolve the highest-quality voice available to the OS / browser.
  // Browsers ship a wide range — robotic default + premium / neural /
  // Siri-quality variants. We pick the best match for the user's
  // language; priority order tested across iOS Safari, macOS Safari,
  // Chrome desktop, and Android Chrome.
  const voicesRef = React.useRef([]);
  const pickedVoiceRef = React.useRef(null);
  React.useEffect(() => {
    if (!ttsSupported) return;
    const synth = window.speechSynthesis;
    const updateVoices = () => {
      const voices = synth.getVoices ? synth.getVoices() : [];
      voicesRef.current = voices;
      pickedVoiceRef.current = pickBestVoice(voices);
    };
    updateVoices();
    // voiceschanged fires async on first page load in most browsers.
    if (typeof synth.addEventListener === 'function') {
      synth.addEventListener('voiceschanged', updateVoices);
      return () => synth.removeEventListener('voiceschanged', updateVoices);
    }
    return undefined;
  }, []);

  // Map a Realtime function tool name to a short user-facing label
  // for the "⚙ <label>…" placeholder we show while the tool runs.
  // Keeps the chat readable without exposing the underscored API id.
  const friendlyToolLabel = (toolName) => {
    const map = {
      spawn_slide_deck:        'Starting the deck creator',
      spawn_proposal_builder:  'Starting the proposal builder',
      people_search:           'Searching the people directory',
      project_search:          'Searching projects',
      client_search:           'Searching clients',
      service_search:          'Searching services',
      industry_search:         'Searching industries',
      web_search:              'Searching the web',
    };
    return map[toolName] || `Running ${toolName.replace(/_/g, ' ')}`;
  };

  // Subtle haptic feedback on key voice transitions — short ping
  // when the mic opens, slightly longer when an utterance is
  // committed, two-pulse on auto-off. Silently no-ops where the
  // Vibration API isn't available (most desktops, all iOS browsers).
  const haptic = React.useCallback((pattern) => {
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate(pattern);
      }
    } catch { /* ignore — vibration is best-effort */ }
  }, []);

  // Helpers for the no-speech auto-off timer. Kept above stopRec so
  // stopRec can clear the timer on every mic teardown.
  const clearNoSpeechTimer = React.useCallback(() => {
    if (noSpeechTimerRef.current) {
      clearTimeout(noSpeechTimerRef.current);
      noSpeechTimerRef.current = null;
    }
  }, []);
  const scheduleNoSpeechTimer = React.useCallback(() => {
    if (noSpeechTimerRef.current) clearTimeout(noSpeechTimerRef.current);
    noSpeechTimerRef.current = setTimeout(() => {
      noSpeechTimerRef.current = null;
      // Double-pulse haptic so the user notices the mic shut down
      // (they may have walked away from the device).
      haptic([40, 60, 40]);
      // Flip back to off — the effect handles stopRec + cancelSpeech.
      // Button repaints to gray + mic-off icon automatically via the
      // class change on the cycle button.
      setVoiceMode('off');
    }, NO_SPEECH_AUTO_OFF_MS);
  }, [haptic]);

  // Handle for the Whisper / MediaRecorder fallback session (used on
  // iOS PWA / Firefox where Web Speech API isn't available).
  const whisperHandleRef = React.useRef(null);
  // Consecutive-empty-transcript counter. Whisper hallucinates default
  // phrases on silence ("Untertitel der Amara.org-Community"); we filter
  // those and they show up as empty here. After 2 in a row we surface
  // a hint; after 5 we give up so the user isn't stuck in a silent loop.
  const emptyTranscriptCountRef = React.useRef(0);

  // Mirror messages into a ref so startRec's useCallback closure can
  // read the latest history (for Whisper's prompt hint) without
  // forcing the callback to re-create on every token tick.
  const messagesRef = React.useRef([]);
  React.useEffect(() => { messagesRef.current = messages; }, [messages]);

  // ─── Realtime API (OpenAI gpt-4o-realtime-preview) ───────────────
  // When available it REPLACES the Whisper + Shimmer-TTS pipeline
  // for conversation mode: single WebSocket, sub-300ms latency,
  // real barge-in. Probed once on mount; falls back gracefully to
  // the existing flow if OPENAI_API_KEY isn't configured or the
  // session can't be established.
  const [realtimeAvailable, setRealtimeAvailable] = React.useState(false);
  const [realtimeTools, setRealtimeTools] = React.useState([]);
  const [realtimeInstructions, setRealtimeInstructions] = React.useState('');
  // Reconnect counter — bumped when the WS closes unexpectedly so
  // the realtime effect re-fires and rebuilds the session.
  const [realtimeReconnectKey, setRealtimeReconnectKey] = React.useState(0);
  // Consecutive-failure tracker. After 3 failed sessions inside
  // 30 s we permanently mark realtime unavailable for THIS session
  // and the conversation cycle falls back to the Whisper+TTS pipeline.
  // Without this cap the chat would loop "connecting → fail → reconnect"
  // forever and the user would never hear voice output.
  const realtimeFailsRef = React.useRef({ count: 0, since: 0 });
  // 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'tool'
  // — drives the composer placeholder so the user always knows
  // what the session is doing.
  const [realtimeState, setRealtimeState] = React.useState('idle');
  const realtimeRef = React.useRef(null);
  const realtimeChatIdRef = React.useRef(chatId || '');
  React.useEffect(() => { realtimeChatIdRef.current = chatId || ''; }, [chatId]);
  React.useEffect(() => {
    let cancelled = false;
    // iOS (Safari + WKWebView in PWA standalone) aggressively closes
    // WebSockets on background, screen lock, even between turns —
    // makes the Realtime streaming-Whisper path unreliable. Force the
    // MediaRecorder + Whisper-batch path on iOS where reliability
    // matters more than the ~500 ms transcript latency saving.
    const isIOS = typeof navigator !== 'undefined'
      && (/iPad|iPhone|iPod/.test(navigator.userAgent)
          || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
    if (isIOS) {
      setRealtimeAvailable(false);
      return;
    }
    RealtimeClient.probe().then((p) => {
      if (cancelled) return;
      setRealtimeAvailable(!!p.available);
      setRealtimeTools(Array.isArray(p.tools) ? p.tools : []);
      setRealtimeInstructions(p.instructions || '');
    });
    return () => { cancelled = true; };
  }, []);
  // Refs the realtime onAssistantTranscriptDelta closure uses to
  // append into the current assistant message in messages state
  // without recreating the client on every render.
  const realtimeMsgIdxRef = React.useRef(-1);

  const stopRec = React.useCallback(() => {
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    clearNoSpeechTimer();
    const rec = recRef.current;
    if (rec) {
      try { rec.onresult = null; rec.onerror = null; rec.onend = null; } catch {}
      try { rec.stop(); } catch {}
      try { rec.abort(); } catch {}
      recRef.current = null;
    }
    // Also tear down any Whisper recording in flight.
    if (whisperHandleRef.current) {
      try { whisperHandleRef.current.cancel(); } catch {}
      whisperHandleRef.current = null;
    }
  }, [clearNoSpeechTimer]);

  // Stable refs the recognizer's event handlers close over.
  const composerRefValue = React.useRef('');
  React.useEffect(() => { composerRefValue.current = composer; }, [composer]);

  const startRec = React.useCallback(() => {
    // STT dispatch — prefer Web Speech API on EVERY mode where it's
    // available. Dictate works flawlessly on it (live interim
    // transcript appears in composer); conversation works flawlessly
    // too (same engine, plus a 1.2 s silence-timer that auto-sends
    // the final transcript). We previously routed conversation
    // through Whisper batch hoping for higher accuracy, but the
    // round-trip introduced silence-hallucination problems while SR
    // was already working fine on user devices. Whisper now ONLY
    // runs when SR is unavailable (iOS PWA standalone, Firefox).
    const wantWhisper = !sttSupported && mediaRecorderSupported;
    if (!wantWhisper && !sttSupported) return;
    if (wantWhisper) {
      // Whisper path: MediaRecorder → /api/stt. Used either as a
      // fallback (no Web Speech API) OR as the chosen primary for
      // conversation mode (better accuracy on names + jargon + DE).
      // VAD auto-stops on silence; conversation mode auto-fires
      // the message, dictate appends to the composer.
      if (whisperHandleRef.current) return;
      // Pass the tail of the latest assistant reply as Whisper's
      // `prompt` parameter. It accepts ~250 tokens of context and
      // uses them to bias transcription toward the conversation's
      // vocabulary — proper nouns, jargon, code-switches between
      // languages all improve. We strip markdown first so the
      // model isn't biased toward backticks / asterisks.
      let promptHint = '';
      try {
        const hist = messagesRef.current;
        for (let i = hist.length - 1; i >= 0 && i >= hist.length - 4; i--) {
          const m = hist[i];
          if (m && m.role === 'assistant' && m.content) {
            promptHint = String(m.content)
              .replace(/```[\s\S]*?```/g, ' ')
              .replace(/[*_`#|]/g, ' ')
              .replace(/\s+/g, ' ')
              .trim()
              .slice(-700);
            break;
          }
        }
      } catch { /* prompt is optional */ }
      startWhisperRecording({
        silenceMs: 1200,
        maxDurationMs: 30_000,
        prompt: promptHint,
        onFinal: (text) => {
          whisperHandleRef.current = null;
          if (!text) {
            // Empty transcript — either pure silence or a Whisper
            // hallucination we filtered (e.g. "Untertitel der
            // Amara.org-Community"). Count consecutive empties and
            // surface a hint if it happens a few times in a row so
            // the user knows their mic isn't picking them up.
            emptyTranscriptCountRef.current += 1;
            if (emptyTranscriptCountRef.current >= 2) {
              setAnimPlaceholder("Didn't catch that — try again");
              setTimeout(() => {
                if (voiceModeRef.current === 'conversation') {
                  setAnimPlaceholder("I'm listening");
                }
              }, 1800);
            }
            // After 5 consecutive empties give up so the user
            // isn't stuck in a silent loop forever.
            if (emptyTranscriptCountRef.current >= 5) {
              emptyTranscriptCountRef.current = 0;
              setVoiceMode('off');
              return;
            }
            if (voiceModeRef.current !== 'off') setTimeout(() => startRec(), 200);
            return;
          }
          // Real speech → reset the empty counter.
          emptyTranscriptCountRef.current = 0;
          // Real speech → reset the auto-off countdown (only matters
          // for the SR path; Whisper path skips this timer entirely
          // because Whisper has its own 30 s maxDuration).
          if (!whisperHandleRef.current) scheduleNoSpeechTimer();
          if (voiceModeRef.current === 'dictate') {
            setComposer(prev => (prev ? prev.replace(/\s+$/, '') + ' ' : '') + text);
            if (voiceModeRef.current !== 'off') setTimeout(() => startRec(), 200);
          } else if (voiceModeRef.current === 'conversation') {
            haptic(15);  // commit pulse
            // Mirror the transcript into the composer for ~1.4 s
            // before clearing — gives the user a visible
            // confirmation that we heard them. The chat history
            // also gets the user message via send() (which now
            // honours keepComposer:true so it doesn't wipe what
            // we just put in).
            setComposer(text);
            sendRef.current?.(text, { keepComposer: true });
            setTimeout(() => {
              setComposer((cur) => (cur === text ? '' : cur));
            }, 1400);
            // Mic stays off through streaming + TTS playback; resumes
            // automatically via drainTtsQueue when the assistant
            // finishes speaking.
          }
        },
        onError: () => {
          whisperHandleRef.current = null;
          // Silent retry — same shape as SR's no-speech path.
          if (voiceModeRef.current !== 'off') setTimeout(() => startRec(), 500);
        },
      }).then((handle) => {
        whisperHandleRef.current = handle;
        // Skip the global 10 s no-speech timer for the Whisper path:
        // startWhisperRecording has its own 30 s maxDuration + 1.2 s
        // VAD silence stop. The outer timer used to fire during a
        // legitimate long utterance and cancel the recording before
        // Whisper could transcribe — exact symptom the user reported
        // ("doesn't show what I say").
      }).catch(() => { /* getUserMedia denied or no audio device */ });
      return;
    }
    if (recRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    // Snapshot whatever was already in the composer so the live
    // transcript is APPENDED rather than overwriting it.
    composerPrefixRef.current = composerRefValue.current ? composerRefValue.current.replace(/\s+$/, '') + ' ' : '';
    finalTextRef.current = '';
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || 'en-US';
    rec.onresult = (ev) => {
      if (voiceModeRef.current === 'off') return;
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (r.isFinal) finalTextRef.current += r[0].transcript;
        else interim += r[0].transcript;
      }
      const merged = (finalTextRef.current + ' ' + interim).trim();
      // Real speech activity → push the auto-off timer back to a
      // full 10 s. Skip the reschedule on truly empty events (some
      // iOS builds fire onresult with zero-length transcripts when
      // the audio session reopens, which would falsely extend the
      // mic session even on silence).
      if (merged.length > 0) scheduleNoSpeechTimer();
      setComposer((composerPrefixRef.current + merged).trim());
      // Voice command: "stop" / "exit" / "stop conversation" /
      // "stop chat" / "exit conversation" as the WHOLE utterance
      // turns voice mode off. Whole-transcript match so "stop the
      // timer" inside a longer sentence doesn't trigger it.
      const lower = merged.toLowerCase().trim().replace(/[.,!?;:]+$/, '');
      if (lower === 'stop' || lower === 'exit' || lower === 'stop conversation' || lower === 'stop chat' || lower === 'exit conversation') {
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        finalTextRef.current = '';
        composerPrefixRef.current = '';
        setComposer('');
        // Defer so the recognizer's own teardown finishes before
        // the React state change triggers stopRec via the
        // voiceMode effect.
        setTimeout(() => setVoiceMode('off'), 50);
        return;
      }
      // Conversation-mode auto-send: 1.2 s of silence commits.
      // OpenAI's voice mode commits on end-of-thought detection at
      // ~0.8-1.2 s; we approximate that with a tighter silence window
      // so turn-taking feels conversational instead of laggy.
      if (voiceModeRef.current === 'conversation') {
        if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
        if (merged.length > 0) {
          silenceTimerRef.current = setTimeout(() => {
            if (voiceModeRef.current !== 'conversation') return;
            const transcript = (composerPrefixRef.current + (finalTextRef.current + ' ' + interim).trim()).trim();
            if (!transcript) return;
            // Reset buffers so the next utterance starts clean.
            composerPrefixRef.current = '';
            finalTextRef.current = '';
            // Kill the mic NOW — we used to play a spoken "got it"
            // ack here, but that ack restarted the mic ~1 s later
            // which then captured the streaming TTS audio coming
            // out of the speaker, generated phantom transcripts,
            // and broke turn 2. The visible user message in the
            // chat history is acknowledgement enough; the spoken
            // result summary plays after streaming finishes and
            // re-opens the mic exactly once via the summary effect.
            haptic(15);  // short pulse — "got it, sending"
            stopRec();
            sendRef.current?.(transcript);
          }, 1200);
        }
      }
    };
    rec.onerror = (ev) => {
      const code = ev?.error || 'recognition-error';
      if (code === 'no-speech' || code === 'aborted') {
        recRef.current = null;
        if (voiceModeRef.current !== 'off') setTimeout(() => startRec(), 200);
        return;
      }
      // Silently mark the rec as dead — no popup. The voiceMode flip
      // to 'off' below is the user-visible signal.
      recRef.current = null;
      // For any non-recoverable error (audio-capture, network,
      // permission denied) flip voiceMode back to off so the cycle
      // button repaints to the gray no-mic state. Without this the
      // mic was effectively dead but the button kept showing the
      // orange/pulse active state, so the user thought it was still
      // listening.
      setVoiceMode('off');
    };
    rec.onend = () => {
      recRef.current = null;
      // Idle restart while voice mode is still on.
      if (voiceModeRef.current !== 'off') setTimeout(() => startRec(), 150);
    };
    try {
      rec.start();
      recRef.current = rec;
      // Start the no-speech auto-off countdown only on the very
      // FIRST opening of the mic for this voiceMode session. We
      // detect "first open" via noSpeechTimerRef === null: subsequent
      // restarts triggered by onerror('no-speech') or onend MUST
      // NOT reset the timer or we'd never auto-off (iOS hits these
      // ~every 5 s of silence). The timer only resets on real user
      // speech in onresult above, or when stopRec runs.
      if (!noSpeechTimerRef.current) scheduleNoSpeechTimer();
    } catch {
      setTimeout(() => startRec(), 250);
    }
  }, [scheduleNoSpeechTimer]);

  // TTS helper. On iOS Safari, an active SpeechRecognition session
  // conflicts with SpeechSynthesis — speak() silently drops the
  // utterance while the mic is capturing. So we PAUSE the rec
  // before each utterance and restart it from utterance.onend so
  // the user can continue talking after the AI replies.
  //
  // Two-tier strategy:
  //   1. PREFER cloud TTS (OpenAI Shimmer via /api/tts) — same voice
  //      across every device, no iOS Siri voice drift.
  //   2. FALLBACK to window.speechSynthesis when the proxy returns
  //      503 (key not configured) OR when audio playback fails (e.g.
  //      silent-switch on, no audio output, network error). The
  //      OS path keeps voice usable even before OPENAI_API_KEY is
  //      provisioned in Cloudflare.
  //
  // We DO NOT cancel any in-flight OS speech — on iOS, cancel
  // sometimes resets the audio-context unlock established by the
  // entry greeting, killing all subsequent utterances.
  // Defined AFTER stopRec/startRec to avoid TDZ on useCallback deps.
  const speakBrief = React.useCallback((text) => {
    if (!text) return;
    const resume = () => {
      if (voiceModeRef.current !== 'off') {
        setTimeout(() => startRec(), 400);
      }
    };
    stopRec();
    // Tier 1: cloud TTS. The promise resolves on playback end so
    // resume() lands at the right moment.
    speakCloud(text, { voice: 'shimmer' }).then(resume).catch(() => {
      // Tier 2: OS speechSynthesis.
      if (!ttsSupported) { resume(); return; }
      try {
        const synth = window.speechSynthesis;
        const u = new SpeechSynthesisUtterance(text);
        u.lang = navigator.language || 'en-US';
        if (pickedVoiceRef.current) u.voice = pickedVoiceRef.current;
        u.onend = resume;
        u.onerror = resume;
        try { synth.resume(); } catch {}
        synth.speak(u);
      } catch { resume(); }
    });
  }, [stopRec, startRec]);

  // React to voiceMode changes — start/stop the recognizer and
  // animate the placeholder typewriter.
  React.useEffect(() => {
    if (voiceMode === 'off') {
      stopRec();
      setAnimPlaceholder('Ask me anything');
      return;
    }
    // For ANY mode transition (off→dictate, dictate→conversation,
    // conversation→dictate) we tear down the previous recognizer
    // first. Otherwise a dictate→conversation flip would leave the
    // dictate-rec running while we set up the greeting, and the
    // dictate-rec would capture the greeting itself as a phantom
    // transcript that auto-sends after the 2.5 s silence.
    stopRec();
    // Realtime path: when entering CONVERSATION mode AND the
    // realtime backend is available, the WebSocket effect below
    // owns the mic + audio. Skip the SR/Whisper startRec — that
    // would double-acquire the mic and confuse iOS audio session.
    if (voiceMode === 'conversation' && realtimeAvailable) {
      // Realtime effect handles greeting + mic + TTS — nothing to
      // start here. We still let the typewriter run below so the
      // placeholder animates the same way.
    } else if (!greetingActiveRef.current) {
      startRec();
    }
    if (voiceMode === 'conversation') {
      // Don't speak the LAST already-shown assistant reply — only new
      // replies generated WHILE conversation mode is on.
      lastSpokenIdxRef.current = messages.length - 1;
      // Typewriter: animate from current placeholder to "I'm listening".
      // The realtime-state effect below overwrites it with live status
      // (Connecting / Listening / Thinking / Speaking) once the
      // realtime session is up.
      const target = "I'm listening";
      let i = 0;
      setAnimPlaceholder('');
      const id = setInterval(() => {
        i++;
        setAnimPlaceholder(target.slice(0, i));
        if (i >= target.length) clearInterval(id);
      }, 55);
      return () => clearInterval(id);
    } else {
      setAnimPlaceholder('Ask me anything');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMode, startRec, stopRec]);

  // Live placeholder for the realtime session — overwrites the
  // typewriter "I'm listening" once the session reports its state.
  // Without this the user has no visible signal that the assistant
  // is currently thinking / speaking after they stopped talking.
  React.useEffect(() => {
    if (voiceMode !== 'conversation' || !realtimeAvailable) return;
    const label =
      realtimeState === 'connecting' ? 'Connecting…' :
      realtimeState === 'thinking'   ? 'Thinking…' :
      realtimeState === 'tool'       ? 'Working on it…' :
      realtimeState === 'speaking'   ? 'Speaking…' :
      realtimeState === 'listening'  ? "I'm listening" :
                                       "I'm listening";
    setAnimPlaceholder(label);
  }, [voiceMode, realtimeAvailable, realtimeState]);

  // Cycle handler for the mic button: off → dictate → conversation → off.
  const cycleVoiceMode = React.useCallback(() => {
    // (Removed: an OS-TTS "OK" warmup that used to fire here. Cloud
    // TTS is now primary, so the iOS gesture-unlock requirement is
    // satisfied by the audible greeting below — and the warmup was
    // pinning the AVAudioSession into playback mode at the moment
    // the recognizer needed it.)
    setVoiceMode(prev => {
      const next = prev === 'off' ? 'dictate' : prev === 'dictate' ? 'conversation' : 'off';
      if (next === 'conversation') {
        // Short rising-tone "mode on" cue. Replaces the spoken
        // "I'm listening" greeting that the user found too slow.
        // Plays from inside the click gesture so iOS audio is
        // unlocked for the rest of the session.
        playOnTone();
        haptic(20);
        if (realtimeAvailable) {
          // Realtime effect handles the session — no startRec here.
          return next;
        }
        greetingActiveRef.current = true;
        const startMic = () => {
          greetingActiveRef.current = false;
          if (voiceModeRef.current === 'conversation') startRec();
        };
        // No spoken greeting anymore — the tone above is the cue.
        // Wait ~250 ms for the tone to finish + the audio session to
        // settle, then open the mic.
        setTimeout(startMic, 250);
        // Pre-acquire the persistent mic stream so the first
        // recording doesn't pay the ~300 ms getUserMedia tax.
        acquireMicStream().catch(() => undefined);
      } else {
        // Leaving conversation mode → falling "mode off" tone +
        // silence any in-flight speech + release the mic so iOS
        // drops the red "in use" indicator.
        if (prev === 'conversation') playOffTone();
        greetingActiveRef.current = false;
        try { window.speechSynthesis.cancel(); } catch {}
        try { cancelSpeech(); } catch {}
        if (next === 'off') releaseMicStream();
      }
      return next;
    });
  }, [startRec]);

  // ── Streaming TTS pipeline ──────────────────────────────────────
  // OpenAI-style: the assistant starts SPEAKING within ~1 s of
  // beginning to generate, not after streaming completes. We watch
  // the latest assistant message; as new sentences land, we enqueue
  // them; a drain loop plays them sequentially. While streaming we
  // only extract COMPLETE sentences (terminated by .!? + space) so
  // we don't speak partial words; the final flush when streaming
  // ends mops up any trailing fragment.
  const ttsHeadRef = React.useRef(0);      // chars of CLEANED content we've enqueued
  const ttsQueueRef = React.useRef([]);    // pending speech chunks
  const ttsPlayingRef = React.useRef(false);
  const streamingRef = React.useRef(false);
  React.useEffect(() => { streamingRef.current = streaming; }, [streaming]);

  // Common abbreviations whose periods would otherwise be mistaken
  // for sentence ends by the streaming sentence-splitter. We swap
  // their dots for a sentinel character (․ = "one dot leader")
  // before the splitter runs, then swap back for TTS.
  // Covers DE + EN common abbreviations.
  const ABBREV_REGEX = /\b(z\.B|d\.h|u\.a|u\.U|bzw|usw|ggf|bspw|evtl|bzgl|i\.d\.R|n\.Chr|v\.Chr|St|Hr|Fr|Dr|Prof|Mr|Mrs|Ms|Inc|Ltd|Co|St|Mt|e\.g|i\.e|etc|vs|cf|et\sal|p\.m|a\.m|U\.S|U\.K|U\.S\.A|approx|min|max|no|incl|excl)\./gi;
  const ABBREV_REGEX_RESTORE = /․/g;

  const stripForVoice = React.useCallback((s) => String(s)
    .replace(/```[\s\S]*?```/g, ' (code block) ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\|/g, ' ')
    .replace(/-{3,}/g, '')
    .replace(/\[\[[^\]]+\]\]/g, '')
    // Protect abbreviation dots BEFORE we collapse newlines into '.':
    // otherwise "z.B." in mid-sentence would trigger a premature split.
    .replace(ABBREV_REGEX, (m) => m.replace(/\./g, '․'))
    .replace(/\n+/g, '. ')
    .replace(/\s+/g, ' '), []);

  // Restore protected abbreviation dots back to literal '.' just
  // before handing text to the TTS endpoint. The splitter already
  // ran on the protected form, so dots inside abbreviations stayed
  // together with their preceding token — no false sentence breaks.
  const restoreAbbrevs = React.useCallback((s) => s.replace(ABBREV_REGEX_RESTORE, '.'), []);

  // Cancel any in-flight TTS playback + drain the queue. Called when
  // the user taps the mic button (voiceMode → 'off' or 'dictate') so
  // a long assistant reply doesn't keep speaking after they bail.
  const stopTtsPlayback = React.useCallback(() => {
    ttsQueueRef.current = [];
    try { cancelSpeech(); } catch {}
    try { if (typeof window !== 'undefined') window.speechSynthesis?.cancel(); } catch {}
  }, []);

  const drainTtsQueue = React.useCallback(async () => {
    if (ttsPlayingRef.current) return;
    if (ttsQueueRef.current.length === 0) return;
    ttsPlayingRef.current = true;
    // Drive the placeholder via realtimeState so the user sees
    // "Speaking…" instead of staying on "Thinking…" through the
    // TTS playback. Only matters when a realtime session owns the
    // input pipe (hybrid path) — Whisper+TTS-only mode runs
    // without the realtime client at all.
    if (realtimeRef.current) setRealtimeState('speaking');
    // Mic must stay off for the full duration of TTS playback —
    // otherwise it echoes through the speaker into the recognizer.
    stopRec();

    // Gapless playback via prefetch: we always have the NEXT chunk's
    // audio bytes downloading while the current chunk plays. Cuts
    // inter-chunk silence from ~300 ms (single-shot fetch+play) to
    // effectively zero on stable connections.
    let nextPrefetch = null;
    const kickoff = (text) => {
      try { return prefetchTtsPayload(text, { voice: 'shimmer' }); }
      catch { return Promise.reject(new Error('prefetch failed')); }
    };

    // Seed the first prefetch.
    if (ttsQueueRef.current.length > 0) {
      nextPrefetch = kickoff(restoreAbbrevs(ttsQueueRef.current[0]));
    }

    while (ttsQueueRef.current.length > 0) {
      if (voiceModeRef.current === 'off') { ttsQueueRef.current = []; break; }
      const text = restoreAbbrevs(ttsQueueRef.current.shift() || '');
      if (!text) continue;
      // Resolve current chunk's payload + start prefetching the next
      // one IN PARALLEL with the playback below. Awaiting nextPrefetch
      // here means the network round-trip overlaps with prior audio,
      // so the moment one chunk ends the next is ready to play.
      let payload = null;
      try { payload = await nextPrefetch; } catch { payload = null; }
      nextPrefetch = ttsQueueRef.current.length > 0
        ? kickoff(restoreAbbrevs(ttsQueueRef.current[0]))
        : null;
      try {
        if (payload) {
          await playTtsPayload(payload);
        } else {
          // Cloud prefetch failed — try OS fallback for THIS chunk.
          await new Promise((res) => {
            if (!ttsSupported) { res(); return; }
            try {
              const synth = window.speechSynthesis;
              const u = new SpeechSynthesisUtterance(text);
              u.lang = navigator.language || 'en-US';
              if (pickedVoiceRef.current) u.voice = pickedVoiceRef.current;
              u.onend = res;
              u.onerror = res;
              try { synth.resume(); } catch {}
              synth.speak(u);
            } catch { res(); }
          });
        }
      } catch {
        // Playback failed mid-chunk — skip ahead. The OS fallback
        // already ran for fetch failures above; this catches the
        // rarer "audio play denied" case (e.g. user revoked the
        // audio session). The next chunk in queue still tries.
      }
    }
    ttsPlayingRef.current = false;
    // Resume the mic when streaming is done AND the queue is empty.
    // If streaming is still in flight, the next message-watcher tick
    // will fire drainTtsQueue again — keep the mic off until then.
    if (voiceModeRef.current === 'conversation'
        && !streamingRef.current
        && ttsQueueRef.current.length === 0) {
      // Hybrid: if a Realtime session is active, re-enable its mic
      // input rather than starting a separate recogniser. The
      // session has been listening continuously (just gated) so
      // the next user turn flows without re-init lag.
      if (realtimeRef.current) {
        try { realtimeRef.current.setInputEnabled(true); } catch {}
        setRealtimeState('listening');
      } else {
        setTimeout(() => startRec(), 400);
      }
    }
  }, [stopRec, startRec, restoreAbbrevs]);

  // Watcher: extract complete sentences from the latest assistant
  // message as it grows. Splits into ≤600-char chunks so each
  // speakCloud round-trip is responsive (Shimmer's first byte
  // lands within ~300 ms).
  React.useEffect(() => {
    if (voiceMode !== 'conversation') return;
    // Hybrid path: Realtime is the streaming-Whisper mic; Claude
    // generates the response via /api/chat; this watcher speaks it
    // via /api/tts (Shimmer) chunked. So the TTS effect runs in
    // BOTH paths now — no early return.
    const lastIdx = messages.length - 1;
    if (lastIdx < 0) return;
    const last = messages[lastIdx];
    if (!last || last.role !== 'assistant' || !last.content) return;

    // New assistant message? Reset chunking state.
    if (lastIdx > lastSpokenIdxRef.current) {
      lastSpokenIdxRef.current = lastIdx;
      ttsHeadRef.current = 0;
      ttsQueueRef.current = [];
    }

    const cleaned = stripForVoice(last.content);
    if (cleaned.length <= ttsHeadRef.current) return;
    const delta = cleaned.slice(ttsHeadRef.current);

    let extracted = '';
    if (streamingRef.current) {
      // Only extract COMPLETE sentences while still streaming —
      // tail stays for next pass so we don't speak mid-word.
      const matches = delta.match(/[^.!?]+[.!?]+\s/g);
      if (matches) extracted = matches.join('');
    } else {
      // Stream done — flush whatever's left, even fragments.
      extracted = delta;
    }
    if (!extracted.trim()) return;
    ttsHeadRef.current += extracted.length;

    // Pack sentences into chunks of ≤600 chars on sentence boundaries.
    const sentences = extracted.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g) || [extracted];
    let buf = '';
    for (const sentence of sentences) {
      if ((buf + sentence).length > 600 && buf) {
        ttsQueueRef.current.push(buf.trim());
        buf = '';
      }
      buf += sentence;
    }
    if (buf.trim()) ttsQueueRef.current.push(buf.trim());

    drainTtsQueue();
  }, [voiceMode, messages, streaming, stripForVoice, drainTtsQueue]);

  // Safety net: when streaming flips false but nothing was queued
  // (e.g. empty assistant reply), still resume the mic so the
  // conversation doesn't stall. (Realtime path doesn't go through
  // /api/chat streaming, so this is a no-op there.)
  const prevStreamingRef = React.useRef(false);
  React.useEffect(() => {
    const wasStreaming = prevStreamingRef.current;
    prevStreamingRef.current = streaming;
    if (voiceMode !== 'conversation') return;
    if (realtimeAvailable) return;
    if (!wasStreaming || streaming) return;
    if (ttsPlayingRef.current) return;
    if (ttsQueueRef.current.length > 0) return;
    // Nothing to speak and stream just ended — re-open the mic.
    // Realtime path: re-enable WS input; otherwise start a fresh
    // SR/Whisper recording.
    if (realtimeRef.current) {
      try { realtimeRef.current.setInputEnabled(true); } catch {}
      setRealtimeState('listening');
    } else {
      setTimeout(() => startRec(), 400);
    }
  }, [voiceMode, streaming, startRec]);

  // Tap-to-interrupt: when the user cycles voice mode away from
  // conversation, drop any queued + in-flight TTS so the assistant
  // shuts up immediately. Without this, a long reply keeps speaking
  // after the user taps the button to bail.
  React.useEffect(() => {
    if (voiceMode !== 'conversation') stopTtsPlayback();
  }, [voiceMode, stopTtsPlayback]);

  // Realtime session lifecycle. Connect when entering conversation
  // mode (and the backend is available); disconnect on exit. Each
  // completed turn is mirrored into the chat history + persisted
  // to D1 via /api/chat/realtime-turn.
  React.useEffect(() => {
    if (voiceMode !== 'conversation' || !realtimeAvailable) {
      if (realtimeRef.current) {
        realtimeRef.current.close();
        realtimeRef.current = null;
      }
      return;
    }
    if (realtimeRef.current) return;  // already connected

    let cancelled = false;
    let assistantPending = '';
    realtimeMsgIdxRef.current = -1;

    const persistTurn = async (role, content) => {
      if (!content) return;
      try {
        const res = await fetch('/api/chat/realtime-turn', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            chatId: realtimeChatIdRef.current || undefined,
            role,
            content,
          }),
        });
        if (!res.ok) return;
        const j = await res.json();
        if (j.chatId && j.chatId !== realtimeChatIdRef.current) {
          realtimeChatIdRef.current = j.chatId;
          // Tell the parent chat the new id so the sidebar refreshes.
          onChatCreated?.(j.chatId);
        }
        onChatTouched?.();
      } catch { /* best-effort persistence */ }
    };

    setRealtimeState('connecting');
    // Track placeholder messages we inject when a tool starts so
    // we can swap them for the real output (or drop them on error).
    const toolPlaceholderIdxByCallId = new Map();
    // Closure flag for onClose to know whether this session ever
    // became ready (failed-before-ready vs failed-after-ready).
    let sessionReady = false;
    const client = new RealtimeClient({
      voice: 'shimmer',
      tools: realtimeTools,
      instructions: realtimeInstructions,
      chatId: realtimeChatIdRef.current || undefined,
      // Seed the realtime session with the last ~10 chat turns so
      // the model has context (especially when the user switches
      // from typed → voice or reopens an existing chat).
      priorMessages: (messagesRef.current || []).map(m => ({
        role: m.role,
        content: typeof m.content === 'string' ? m.content : '',
      })),
      onToolCallStart: (name, _args, callId) => {
        if (cancelled) return;
        setRealtimeState('tool');
        // Drop a "working on it" placeholder into the chat so the
        // user sees the tool is firing — important on long calls
        // like spawn-slide-deck where the actual output lands 5-15s
        // later. Stash the index by call_id so onToolCallDone can
        // swap the placeholder for the real output instead of
        // appending a second row.
        const placeholderId = crypto.randomUUID();
        const label = friendlyToolLabel(name);
        setMessages((prev) => {
          const next = [
            ...prev,
            {
              id: placeholderId,
              role: 'assistant',
              content: `_⚙ ${label}…_`,
              createdAt: new Date(),
              _realtime: true,
              _toolName: name,
              _toolPending: true,
            },
          ];
          toolPlaceholderIdxByCallId.set(callId, next.length - 1);
          return next;
        });
      },
      onToolCallDone: (name, output, callId) => {
        if (cancelled) return;
        if (output) {
          setMessages((prev) => {
            const idx = toolPlaceholderIdxByCallId.get(callId);
            if (idx != null && prev[idx]?._toolPending) {
              const updated = prev.slice();
              updated[idx] = {
                ...prev[idx],
                content: output,
                _toolPending: false,
              };
              return updated;
            }
            // No placeholder (shouldn't happen) — append.
            return [
              ...prev,
              { id: crypto.randomUUID(), role: 'assistant', content: output, createdAt: new Date(), _realtime: true, _toolName: name },
            ];
          });
          persistTurn('assistant', output);
        }
        toolPlaceholderIdxByCallId.delete(callId);
        setRealtimeState('speaking');
      },
      onReady: () => {
        if (cancelled) return;
        sessionReady = true;
        // Reset the fail counter — we got at least one healthy session.
        realtimeFailsRef.current = { count: 0, since: 0 };
        // Don't clobber thinking/speaking if a turn is in flight —
        // the WS may have reconnected mid-conversation; surfacing
        // "listening" would falsely suggest the assistant stopped.
        if (!streamingRef.current && !ttsPlayingRef.current) {
          setRealtimeState('listening');
        }
        haptic(15);
      },
      onUserTranscript: (text) => {
        if (cancelled) return;
        if (!text || !text.trim()) return;
        haptic(15);  // commit pulse
        setRealtimeState('thinking');
        // HYBRID path: Realtime is the streaming-Whisper mic. The
        // transcript goes to Claude via the SAME /api/chat endpoint
        // that handles typed turns — so the brain, the system
        // prompt, and the full tool catalog (data-table, chart-render,
        // spawn-*, search-*, …) are identical to typed chat.
        // sendRef wraps the streamChat call already; we just call it
        // with the transcript as content. Claude's streamed reply
        // goes into the messages array; the streaming-TTS-watcher
        // picks up sentences and speaks them via /api/tts (Shimmer).
        // Pause the Realtime mic input while the assistant is
        // generating + speaking so it doesn't catch its own echo as
        // a phantom user turn.
        try { realtimeRef.current?.setInputEnabled(false); } catch {}
        // Mirror the transcript into the composer for 1.4 s so the
        // user can SEE what we heard, then clear it (the chat
        // history already carries the user message).
        const heard = text.trim();
        setComposer(heard);
        sendRef.current?.(heard, { keepComposer: true });
        setTimeout(() => {
          setComposer((cur) => (cur === heard ? '' : cur));
        }, 1400);
      },
      onAssistantTranscriptDelta: (delta) => {
        if (cancelled) return;
        if (assistantPending === '') setRealtimeState('speaking');
        assistantPending += delta;
        setMessages((prev) => {
          if (realtimeMsgIdxRef.current === -1) {
            const next = [
              ...prev,
              { id: crypto.randomUUID(), role: 'assistant', content: delta, createdAt: new Date(), _realtime: true },
            ];
            realtimeMsgIdxRef.current = next.length - 1;
            return next;
          }
          const idx = realtimeMsgIdxRef.current;
          if (!prev[idx]) return prev;
          const cur = prev[idx];
          const updated = prev.slice();
          updated[idx] = { ...cur, content: cur.content + delta };
          return updated;
        });
      },
      onAssistantTranscriptDone: (text) => {
        if (cancelled) return;
        const final = text || assistantPending;
        if (final) persistTurn('assistant', final);
        assistantPending = '';
        realtimeMsgIdxRef.current = -1;
        setRealtimeState('listening');
      },
      onSpeechStart: () => {
        haptic(10);
        // User barge-in — server cancels the response, we flip back
        // to thinking so the placeholder reflects state correctly.
        setRealtimeState('thinking');
      },
      onError: () => {
        // session watchdog / WS error — close so onClose fires and
        // the fail counter kicks in.
        try { client.close(); } catch {}
      },
      onClose: () => {
        if (cancelled) return;
        // Track consecutive failures: if the session never became
        // ready (no onReady fired), count it. ≥ 3 fails inside 30 s
        // ⇒ give up on realtime for THIS chat session, set
        // realtimeAvailable=false so the conversation cycle falls
        // back to the Whisper+TTS path on the next mic tap.
        const now = Date.now();
        const win = realtimeFailsRef.current;
        if (now - win.since > 30_000) { win.since = now; win.count = 0; }
        if (!sessionReady) {
          win.count++;
          if (win.count >= 3) {
            setRealtimeAvailable(false);
            setRealtimeState('idle');
            // Permanently flipped to the Whisper+TTS path for this
            // session. Kick the recogniser ourselves so the user
            // doesn't have to re-tap — feels seamless: "Connecting…"
            // → tone → "I'm listening" via the fallback pipeline.
            if (voiceModeRef.current === 'conversation') {
              setTimeout(() => startRec(), 250);
            }
            return;
          }
        }
        if (voiceModeRef.current === 'conversation') {
          // Same protection: only flip the visible placeholder to
          // "Connecting…" if we're actually idle. Mid-chat reconnects
          // happen behind the scenes; the user sees the existing
          // Thinking / Speaking state instead of flicker.
          if (!streamingRef.current && !ttsPlayingRef.current) {
            setRealtimeState('connecting');
          }
          setTimeout(() => {
            if (voiceModeRef.current === 'conversation' && !cancelled) {
              setRealtimeReconnectKey((k) => k + 1);
            }
          }, 600);
        } else {
          setRealtimeState('idle');
        }
      },
    });

    client.connect().catch(() => {
      // Hard failure on connect — counts as one fail and triggers
      // onClose via client.close() → fall-back logic above.
      try { client.close(); } catch {}
      setRealtimeState('idle');
    });
    realtimeRef.current = client;

    return () => {
      cancelled = true;
      try { client.close(); } catch {}
      if (realtimeRef.current === client) realtimeRef.current = null;
      setRealtimeState('idle');
    };
  }, [voiceMode, realtimeAvailable, realtimeReconnectKey, realtimeTools, realtimeInstructions, haptic, onChatCreated, onChatTouched]);

  // Stop voice on unmount — also release the persistent mic stream
  // so iOS drops the red "mic in use" indicator the moment the chat
  // view goes away. Includes the Realtime session if one's open.
  React.useEffect(() => () => {
    stopRec();
    try { window.speechSynthesis.cancel(); } catch {}
    try { releaseMicStream(); } catch {}
    try { realtimeRef.current?.close(); } catch {}
    realtimeRef.current = null;
  }, [stopRec]);

  // Belt-and-suspenders: whenever the assistant is streaming, force
  // the mic off. The silence-timer path already calls stopRec before
  // sendRef, but this effect also kills the mic for any other route
  // into streaming (Send button click, retry, etc.) so the speaker
  // never echoes back into the recognizer mid-stream.
  React.useEffect(() => {
    if (voiceMode !== 'conversation') return;
    if (streaming) stopRec();
  }, [voiceMode, streaming, stopRec]);

  // Fully release the microphone when the PWA gets backgrounded or
  // closed. iOS only unmounts React on hard reload, so without this
  // the orange mic indicator stays on at the top of the OS even
  // after the user swipes the app away. visibilitychange covers
  // backgrounding (switch to another app / lock screen); pagehide
  // covers the close-app and reload paths. We force voiceMode to
  // 'off' so the recognizer's auto-restart logic in `startRec.onend`
  // doesn't immediately reopen the session.
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const release = () => {
      stopRec();
      try { window.speechSynthesis.cancel(); } catch {}
      try { releaseMicStream(); } catch {}
      try { realtimeRef.current?.close(); } catch {}
      realtimeRef.current = null;
      setVoiceMode('off');
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') release(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', release);
    window.addEventListener('beforeunload', release);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', release);
      window.removeEventListener('beforeunload', release);
    };
  }, [stopRec]);

  // Indexed by message array index (the trailing assistant message).
  // Cleared whenever the chat changes.
  const [evals, setEvals] = React.useState({});
  React.useEffect(() => { setEvals({}); }, [chatId]);

  // Fullscreen toggle: when on, the chat fills the viewport (over the
  // top bar and side rails). Esc exits. We also flip a class on
  // <body> so global CSS can hide scrollbars on the host page.
  const [fullscreen, setFullscreen] = React.useState(false);
  React.useEffect(() => {
    if (!fullscreen) return;
    const onKey = e => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('chat-fullscreen-on');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('chat-fullscreen-on');
    };
  }, [fullscreen]);

  // Bubble the current chat's attachment set up to the parent so it
  // can render a side panel only when there's something to show.
  React.useEffect(() => {
    if (!onAttachmentsChange) return;
    const seen = new Set();
    const list = [];
    for (const m of messages) {
      for (const a of (m.attachments || [])) {
        if (a.key && !seen.has(a.key)) { seen.add(a.key); list.push(a); }
      }
    }
    onAttachmentsChange(list);
  }, [messages, onAttachmentsChange]);

  // If the topbar search dropped a seed (sessionStorage), prefill the
  // composer for a fresh chat. Cleared after one use so a second visit
  // doesn't replay.
  React.useEffect(() => {
    if (chatId) return;
    try {
      const seed = sessionStorage.getItem('erasphere.chatSeed');
      if (seed) {
        sessionStorage.removeItem('erasphere.chatSeed');
        setComposer(seed);
      }
    } catch {}
  }, [chatId]);

  // Load history when an existing chat is selected; clear when starting fresh.
  // Skip the fetch if this is a chat we just created via streaming — the
  // local `messages` already holds the user+assistant pair, and the
  // server-side INSERT for the assistant message lands a moment after
  // the stream closes (non-blocking accumulator in /api/chat). Refetching
  // here would briefly wipe the answer.
  // Carries the chat's `kind` so the header can attribute Eve chats
  // ("Eve · live") instead of the default ("Live chat"). Reset on
  // chat change.
  const [chatKind, setChatKind] = React.useState('ask');
  // For a direct-message thread, the OTHER participant ({ id, name, photoUrl }).
  const [peer, setPeer] = React.useState(null);
  const isDm = chatKind === 'dm';
  React.useEffect(() => {
    if (!chatId) {
      setMessages([]);
      setChatKind('ask');
      setPeer(null);
      setError(null);
      return;
    }
    if (skipReloadRef.current === chatId) {
      skipReloadRef.current = null;
      return;
    }
    let cancelled = false;
    setLoading(true);
    getChatMessages(chatId)
      .then(({ messages, chat, peer }) => {
        if (cancelled) return;
        setMessages(messages || []);
        setChatKind(chat?.kind || 'ask');
        setPeer(peer || null);
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chatId]);

  // Sticky-stop auto-scroll: the chat snaps to the bottom on every
  // streamed token UNTIL the user scrolls up — at that point we
  // assume they're reading older context and stop fighting them.
  // A NEW message (their own send, or a fresh assistant turn) always
  // resets to bottom regardless of where they were scrolled. Detect
  // "new turn" via a count delta because changes during a streaming
  // response only mutate the last message's `content`, not the array
  // length.
  const [stickToBottom, setStickToBottom] = React.useState(true);
  const lastMessageCountRef = React.useRef(0);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const newTurn = messages.length > lastMessageCountRef.current;
    lastMessageCountRef.current = messages.length;
    if (newTurn) {
      el.scrollTop = el.scrollHeight;
      setStickToBottom(true);
      return;
    }
    if (stickToBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, streaming, stickToBottom]);

  // 30 px deadband from bottom keeps wheel-up from immediately
  // unsticking — small inertia overshoots / mid-token reflows count
  // as "still at bottom".
  const onScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
    if (atBottom !== stickToBottom) setStickToBottom(atBottom);
  }, [stickToBottom]);

  // Elastic overscroll: when the user wheel-scrolls past the top or
  // bottom edge, briefly translate the scroll container with diminishing
  // returns, then spring it back via easeOutCubic. iOS already gives this
  // for free via overscroll-behavior:contain + -webkit-overflow-scrolling
  // — this handler bridges the gap on desktop where browsers stop dead at
  // the boundary of inner scroll elements.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let offset = 0;
    let springRaf = null;
    let wheelTimer = null;
    const MAX = 140;
    const apply = px => { el.style.transform = px ? `translateY(${px}px)` : ''; };
    const cancelSpring = () => { if (springRaf) { cancelAnimationFrame(springRaf); springRaf = null; } };
    const springBack = () => {
      cancelSpring();
      const start = performance.now();
      const startOff = offset;
      const dur = 360;
      const tick = now => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        offset = startOff * (1 - eased);
        apply(offset);
        if (t < 1) springRaf = requestAnimationFrame(tick);
        else { offset = 0; springRaf = null; apply(0); }
      };
      springRaf = requestAnimationFrame(tick);
    };
    const onWheel = (e) => {
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      const pushingTop = atTop && e.deltaY < 0;
      const pushingBottom = atBottom && e.deltaY > 0;
      if (pushingTop || pushingBottom) {
        e.preventDefault();
        cancelSpring();
        // Diminishing returns: harder to push further as offset grows.
        const resistance = 1 - Math.min(Math.abs(offset) / MAX, 0.9);
        offset -= e.deltaY * 0.35 * resistance;
        offset = Math.max(-MAX, Math.min(MAX, offset));
        apply(offset);
        if (wheelTimer) clearTimeout(wheelTimer);
        wheelTimer = setTimeout(springBack, 130);
      } else if (offset !== 0 && !springRaf) {
        // User is scrolling AWAY from the edge — release the spring.
        springBack();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
      cancelSpring();
      if (wheelTimer) clearTimeout(wheelTimer);
      el.style.transform = '';
    };
  }, []);

  const handleFilesPicked = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setError(null);
    const files = Array.from(fileList);
    // Upload sequentially to keep error messages obvious and not hammer R2.
    for (const file of files) {
      setUploading(n => n + 1);
      try {
        const a = await uploadChatFile(file);
        setPendingAttachments(prev => [...prev, a]);
      } catch (err) {
        setError(`upload "${file.name}" failed: ${err.message}`);
      } finally {
        setUploading(n => n - 1);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePending = async att => {
    // Drop from UI immediately; delete from R2 in the background.
    setPendingAttachments(prev => prev.filter(a => a.key !== att.key));
    try { await deleteChatFile({ key: att.key }); }
    catch { /* the orphan sits in R2 until the sweeper picks it up */ }
  };

  const send = async (overrideContent, opts = {}) => {
    const content = (typeof overrideContent === 'string' ? overrideContent : composer).trim();
    if ((!content && pendingAttachments.length === 0) || streaming) return;

    // Direct-message thread: a plain human-to-human post. No Claude, no
    // streaming, no self-eval / TTS — just append the message to the
    // shared thread (the recipient sees it in their normal chat history).
    if (isDm) {
      if (!content || !chatId) return;
      if (!opts.keepComposer) setComposer('');
      setComposerExpanded(false);
      if (composerRef.current) composerRef.current.style.height = '';
      setError(null);
      const optimistic = {
        id: `tmp-${Date.now()}`,
        role: 'user',
        content,
        senderId: user?.id || user?._id,
        attachments: [],
        createdAt: new Date(),
      };
      setMessages(prev => [...prev, optimistic]);
      try {
        const { message } = await postChatMessage(chatId, content);
        if (message) {
          setMessages(prev => prev.map(m => (m.id === optimistic.id ? message : m)));
        }
        onChatTouched?.();
      } catch (err) {
        setError(err.message);
        setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      }
      return;
    }

    const attachmentsForSend = pendingAttachments;
    // Voice-conversation flow passes keepComposer:true so we can
    // BRIEFLY show the heard transcript in the input field as
    // confirmation (then clear it via a setTimeout). Typed flow
    // clears immediately as before.
    if (!opts.keepComposer) setComposer('');
    setComposerExpanded(false);
    if (composerRef.current) composerRef.current.style.height = '';
    setPendingAttachments([]);
    setError(null);
    // Any in-flight TTS from a previous turn — cut it off now so the
    // new question isn't talked over.
    cancelSpeech();
    const userMsg = { role: 'user', content, attachments: attachmentsForSend };
    // `steps`: live tool-call trace for the in-flight assistant message.
    // Each entry: { stepId, skillId, skillName, summary, ok, durationMs, phase }.
    // Rendered above the message bubble while streaming.
    const assistantMsg = { role: 'assistant', content: '', attachments: [], steps: [] };
    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setStreaming(true);

    const ac = new AbortController();
    abortRef.current = ac;
    let aborted = false;
    let finalReply = '';
    try {
      const { chatId: newId, assistantMessageId } = await streamChat({
        chatId,
        content,
        model,
        designSystem,
        attachments: attachmentsForSend,
        skillIds: selectedSkillIds,
        // When the user is in voice conversation mode, hint the
        // backend so the system prompt biases toward short,
        // spoken-friendly responses (no markdown noise, 1-3
        // sentences, conversational tone).
        voiceMode: voiceModeRef.current === 'conversation' ? 'conversation' : undefined,
        signal: ac.signal,
        onDelta: delta => {
          finalReply += delta;
          setMessages(prev => {
            const next = prev.slice();
            const last = next[next.length - 1];
            next[next.length - 1] = { ...last, content: last.content + delta };
            return next;
          });
        },
        onStep: ev => {
          setMessages(prev => {
            const next = prev.slice();
            const lastIdx = next.length - 1;
            const last = next[lastIdx];
            if (!last || last.role !== 'assistant') return prev;
            const steps = last.steps ? last.steps.slice() : [];
            // Run refs surfaced by spawn-* tools the moment they return —
            // lets the live progress card mount mid-turn, not only after
            // the run link finishes streaming into the prose.
            let liveRunRefs = last.liveRunRefs;
            if (ev.type === 'tool_use_start') {
              steps.push({
                stepId: ev.stepId,
                skillId: ev.skillId,
                skillName: ev.skillName,
                summary: ev.summary || '',
                phase: 'running',
                ok: null,
                durationMs: null,
              });
            } else if (ev.type === 'tool_use_end') {
              const i = steps.findIndex(s => s.stepId === ev.stepId);
              if (i >= 0) {
                steps[i] = {
                  ...steps[i],
                  phase: 'done',
                  ok: ev.ok !== false,
                  durationMs: ev.durationMs,
                  resultSummary: ev.summary || '',
                };
              }
              if (ev.runRef && ev.runRef.runId) {
                const cur = liveRunRefs || [];
                if (!cur.some(r => r.runId === ev.runRef.runId)) liveRunRefs = [...cur, ev.runRef];
              }
            } else if (ev.type === 'thinking_start') {
              steps.push({ stepId: `think-${ev.round}`, phase: 'thinking' });
            } else if (ev.type === 'thinking_end') {
              const i = steps.findIndex(s => s.stepId === `think-${ev.round}`);
              if (i >= 0) steps.splice(i, 1);
            } else {
              return prev;
            }
            next[lastIdx] = { ...last, steps, liveRunRefs };
            return next;
          });
        },
      });
      // Attach the assistant message's persisted id so the thumbs
      // widget can target it immediately — otherwise the row had no
      // id until the next reload and ThumbsRow wouldn't render.
      if (assistantMessageId) {
        setMessages(prev => {
          const next = prev.slice();
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = { ...last, id: assistantMessageId };
          }
          return next;
        });
      }
      // Read the reply aloud once the stream is done. We don't speak
      // each delta — speechSynthesis splits awkwardly mid-word, and
      // queueing utterances would clobber each other.
      // TTS is now owned by ConversationMode (chat surface no longer
      // reads replies aloud — that toggle was removed).
      if (!chatId && newId) {
        skipReloadRef.current = newId;
        onChatCreated?.(newId);
      }
      onChatTouched?.();
      // The chat title is summarised by a background Haiku call after
      // each user message. Pick it up by re-polling the chat list a
      // few seconds after the stream settles — gives Haiku time to
      // return + write the new title to D1.
      setTimeout(() => { onChatTouched?.(); }, 2500);

      // Self-learning evaluation: the system agent
      // `self-learning-chat-eval` (admin-toggled) runs server-side
      // when enabled. The chat surface no longer drives it from a
      // per-user opt-in — the endpoint is gated by isSystemAgentEnabled.
      // Always-attempt-then-ignore-403: lets the admin turn it on /
      // off without redeploys, and prevents a 403 from clobbering UX.
      const evalChatId = newId || chatId;
      if (evalChatId) {
        setMessages(prev => {
          const idx = prev.length - 1;
          setEvals(e => ({ ...e, [idx]: { status: 'running' } }));
          return prev;
        });
        (async () => {
          try {
            const result = await evaluateChatReply(evalChatId, {
              assistantText: finalReply,
              userText: content,
              userHadAttachments: attachmentsForSend.length > 0,
            });
            setMessages(prev => {
              const idx = prev.length - 1;
              setEvals(e => ({ ...e, [idx]: { status: 'done', result } }));
              return prev;
            });
          } catch (err) {
            // Disabled by admin (HTTP 403) is a silent expected state.
            const msg = String(err?.message || '');
            if (/\b403\b|disabled/i.test(msg)) {
              setMessages(prev => {
                setEvals(e => { const next = { ...e }; delete next[prev.length - 1]; return next; });
                return prev;
              });
              return;
            }
            setMessages(prev => {
              const idx = prev.length - 1;
              setEvals(e => ({ ...e, [idx]: { status: 'failed', error: err.message } }));
              return prev;
            });
          }
        })();
      }
    } catch (err) {
      if (err?.name === 'AbortError' || ac.signal.aborted) {
        aborted = true;
        // Tag the partial assistant message so the user knows why it stopped.
        setMessages(prev => {
          const next = prev.slice();
          const last = next[next.length - 1];
          const tail = last?.content ? '\n\n— cancelled —' : '— cancelled —';
          next[next.length - 1] = { ...last, content: (last?.content || '') + tail };
          return next;
        });
      } else {
        setError(err.message);
      }
    } finally {
      abortRef.current = null;
      setStreaming(false);
      // Best-effort note: a cancel means the partial reply on the
      // server may or may not have been persisted — we leave it
      // alone here. A future cleanup can reconcile.
      void aborted;
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
    cancelSpeech();
  };

  // Keep the dictation hook's stale-closure copy of `send` fresh.
  sendRef.current = send;

  const handleAttachmentClick = (att, messageId) => {
    setOpenAttachment({ att, messageId });
  };

  const handleAttachmentDeleted = key => {
    setMessages(prev => prev.map(m => ({
      ...m,
      attachments: (m.attachments || []).filter(a => a.key !== key),
    })));
    setOpenAttachment(null);
  };

  return (
    <div className={`chat-main${fullscreen ? ' chat-main--fullscreen' : ''}`}>
      {/* Floating chat-history toggle — round glass button top-left.
          Distinct from the main-nav hamburger in TopHeader; this one
          opens the chats drawer specifically. Visible on mobile only;
          desktop has the chat list rail permanently visible. */}
      {onOpenList && (
        <button
          type="button"
          className="chat-history-btn"
          onClick={onOpenList}
          aria-label="Open chat history"
          title="Chat history"
        >
          <Icon name="chat" size={16}/>
        </button>
      )}

      {/* Streaming indicator — when a reply is in flight, sit a small
          pill near the top so the user knows it's working. */}
      {streaming && (
        <div className="chat-streaming-banner" aria-live="polite">
          <span className="live-dot"/>
          {chatKind === 'eve-nudge' ? 'Eve is thinking…' : 'Streaming from Claude…'}
        </div>
      )}

      <div className="chat-main__scroll" ref={scrollRef} onScroll={onScroll} style={{position:'relative'}}>
        {loading && (
          <div style={{padding:'40px 20px',textAlign:'center',color:'var(--fg-muted)',fontSize:14}}>
            Loading messages…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <ChatGreeting user={user} chatKind={chatKind} peer={peer}/>
        )}
        <div style={{display:'flex',flexDirection:'column',gap:18,paddingBottom:24}}>
          {messages.map((m, i) => {
            // Direct-message thread: align by author (sender_id), not role —
            // both sides are stored as role='user'. The viewer's own
            // messages sit on the right; the peer's on the left.
            if (isDm) {
              const mine = (m.senderId || m.sender_id) === user.id;
              // A deck-share DM carries the viewer link on its own line; lift
              // it into an "Open slide deck" button and drop the raw URL from
              // the visible text.
              const { text, deckUrl } = extractDeckRef(m.content);
              return (
                <DmMsg
                  key={i}
                  mine={mine}
                  authorName={mine ? user.name : (peer?.name || 'Unknown user')}
                  authorImg={mine ? (user.photoUrl || null) : (peer?.photoUrl || null)}
                  deckUrl={deckUrl}
                >
                  {text}
                </DmMsg>
              );
            }
            if (m.role === 'user') {
              return <UserMsg key={i} user={user} attachments={m.attachments} messageId={m.id} onAttClick={handleAttachmentClick}>{m.content}</UserMsg>;
            }
            const isCurrentStreaming = streaming && i === messages.length - 1;
            const priorUser = i > 0 ? messages[i - 1] : null;
            return (
              <React.Fragment key={i}>
                <EveMsg
                  content={m.content}
                  streaming={isCurrentStreaming}
                  steps={m.steps}
                  liveRunRefs={m.liveRunRefs}
                  onAction={label => sendRef.current?.(label)}
                  feedbackTarget={!isCurrentStreaming && m.id ? {
                    type: 'chat_message',
                    id: m.id,
                    label: (m.content || '').slice(0, 120),
                  } : null}
                  feedbackSnapshots={!isCurrentStreaming && m.id ? {
                    intent: priorUser?.content ? `User asked: "${priorUser.content.slice(0, 600)}"` : null,
                    aiDid: m.content || '',
                    link: `#chats`,
                  } : null}
                />
                {evals[i] && <SelfLearningCard eval={evals[i]}/>}
              </React.Fragment>
            );
          })}
          {error && (
            <div style={{padding:'10px 14px',background:'#FCE6E0',color:'#A20235',borderRadius:10,fontSize:13}}>
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Floating "↓ Latest" pill — appears only when the user has
          scrolled up off the bottom. One click re-sticks and snaps
          to the newest message. */}
      {!stickToBottom && messages.length > 0 && (
        <button
          type="button"
          className="chat-latest-btn"
          onClick={() => {
            const el = scrollRef.current;
            if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            setStickToBottom(true);
          }}
          aria-label="Jump to latest message"
        >
          <Icon name="arrow-down" size={14}/>
          <span>Latest{streaming ? ' · streaming' : ''}</span>
        </button>
      )}

      {composerExpanded && (
        <div
          className="chat-composer__backdrop"
          onClick={() => setComposerExpanded(false)}
          aria-hidden="true"
        />
      )}

      <div className={`chat-composer${composerExpanded ? ' chat-composer--expanded' : ''}${voiceMode === 'conversation' ? ' is-conversation' : ''}`}>
        <button
          type="button"
          className="composer-expand-btn"
          onClick={() => setComposerExpanded(v => !v)}
          aria-label={composerExpanded ? 'Collapse composer' : 'Expand composer to fullscreen'}
          title={composerExpanded ? 'Collapse (Esc)' : 'Expand to fullscreen'}
        >
          <Icon name={composerExpanded ? 'collapse' : 'expand'} size={14}/>
        </button>

        <textarea
          ref={composerRef}
          className={`chat-composer__input${composerExpanded ? ' chat-composer__input--expanded' : ''}`}
          rows={2}
          placeholder={isDm ? `Message ${peer?.name ? peer.name.split(/\s+/)[0] : ''}`.trim() : (voiceMode === 'conversation' ? animPlaceholder : 'Ask me anything')}
          value={composer}
          disabled={streaming}
          onChange={e => setComposer(e.target.value)}
          onKeyDown={e => {
            // Cmd/Ctrl+Enter still sends as a power-user shortcut.
            // Plain Enter always inserts a newline so the user can
            // draft multi-line prompts without accidentally sending.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
          }}
        />

        {/* Voice-mode hints. State 2 = dictate (writes into composer,
            you tap Send). State 3 = conversation (auto-send + TTS).
            Also flag explicit TTS/STT support gaps so the user
            isn't waiting on audio that can't play. */}
        {/* Voice hint banners removed per user request — the cycle
            button's icon + pulsing glow already signal the active
            state; an explicit "Voice playback not supported" banner
            is overkill for the rare browser missing speechSynthesis. */}

        {(pendingAttachments.length > 0 || uploading > 0) && (
          <div className="chat-composer__attachments">
            {pendingAttachments.map(a => (
              <AttachmentChip
                key={a.key}
                a={a}
                pending
                onRemove={() => removePending(a)}
              />
            ))}
            {uploading > 0 && (
              <span style={{fontSize:11.5,color:'var(--fg-muted)'}}>uploading {uploading}…</span>
            )}
          </div>
        )}

        <div className="chat-composer__actions">
          {/* File attachments are an AI-chat affordance; a direct message
              is a plain text note, so the attach control is hidden there. */}
          {!isDm && (
            <>
              <button
                type="button"
                className="composer-icon-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={streaming}
                title="Attach files"
                aria-label="Attach files"
              >
                <Icon name="upload" size={18}/>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{display:'none'}}
                onChange={e => handleFilesPicked(e.target.files)}
              />
            </>
          )}

          {/* Model + skill pickers are AI-chat concerns — hidden in a
              direct-message thread, which is a plain note to a colleague. */}
          {!isDm && (
            <select
              className="composer-chip composer-chip--select"
              value={model}
              onChange={e => setModel(e.target.value)}
              disabled={streaming}
              title={CLAUDE_MODELS.find(m => m.id === model)?.tagline}
              aria-label="Claude model"
            >
              {CLAUDE_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          )}

          {!isDm && (
            <SkillsPicker
              catalog={skillCatalog}
              selectedIds={selectedSkillIds}
              onChange={setSelectedSkillIds}
              open={skillsOpen}
              setOpen={setSkillsOpen}
              disabled={streaming}
              designSystem={designSystem}
              setDesignSystem={setDesignSystem}
            />
          )}

          <div style={{flex:1}}/>

          {/* 3-state voice cycle: off → dictate → conversation → off.
              - off:          mic-off icon (slashed), neutral colour
              - dictate:      mic icon, orange (active dictation; no auto-send)
              - conversation: waveform icon, orange + glow; auto-send on
                              2.5 s silence, TTS speaks a brief summary */}
          {!isDm && (
            <button
              type="button"
              className={`composer-voice-cycle composer-voice-cycle--${voiceMode}`}
              onClick={cycleVoiceMode}
              title={
                voiceMode === 'off'        ? 'Tap to enable mic (dictation only — you send manually)'
                : voiceMode === 'dictate'  ? 'Mic on — tap again for conversation mode (auto-send + voice replies)'
                :                            'Conversation mode — tap to turn voice off'
              }
              aria-label={
                voiceMode === 'off' ? 'Enable microphone'
                : voiceMode === 'dictate' ? 'Switch to conversation mode'
                : 'Turn voice off'
              }
            >
              <Icon name={voiceMode === 'off' ? 'mic-off' : voiceMode === 'dictate' ? 'mic' : 'waveform'} size={22}/>
            </button>
          )}

          {streaming ? (
            <button
              className="composer-send-btn composer-send-btn--stop"
              onClick={cancel}
              title="Stop the in-flight response"
              aria-label="Stop streaming"
            >
              <span className="composer-stop-square"/>
            </button>
          ) : (
            <button
              className="composer-send-btn"
              onClick={send}
              disabled={!composer.trim() && pendingAttachments.length === 0}
              aria-label="Send message"
            >
              <Icon name="send" size={16}/>
            </button>
          )}
        </div>
      </div>

      {openAttachment && (
        <AttachmentModal
          attachment={openAttachment.att}
          messageId={openAttachment.messageId}
          onClose={() => setOpenAttachment(null)}
          onDeleted={handleAttachmentDeleted}
        />
      )}
    </div>
  );
};

/* ── attachment chip + modal ────────────────────────────────── */

/* MIME → file icon mapping lives in primitives.jsx (iconNameForMime +
   <MimeIcon/>). Video / audio / archive mimes fall through to the
   generic file-text icon — distinct icons would need separate SVG
   paths and aren't worth carrying until we expose those uploads. */

function fmtBytes(n) {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

const AttachmentChip = ({ a, pending = false, onClick, onRemove }) => (
  <span
    style={{
      display:'inline-flex',alignItems:'center',gap:6,
      padding:'4px 10px',
      background: pending ? 'var(--bg-alt)' : 'var(--bg-card)',
      border:'1px solid var(--border)',
      borderRadius:14,
      fontSize:12,
      cursor: onClick ? 'pointer' : 'default',
      maxWidth:240,
    }}
    onClick={onClick}
    title={`${a.name}\n${a.mime} · ${fmtBytes(a.size)}`}
  >
    <MimeIcon mime={a.mime} size={13} tint="var(--fg-muted)"/>
    <span style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.name}</span>
    <span style={{color:'var(--fg-muted)',fontSize:10.5,whiteSpace:'nowrap'}}>{fmtBytes(a.size)}</span>
    {onRemove && (
      <span
        onClick={e => { e.stopPropagation(); onRemove(); }}
        style={{cursor:'pointer',color:'var(--fg-muted)',marginLeft:2,padding:'0 2px'}}
        title="Remove"
      >×</span>
    )}
  </span>
);

const AttachmentModal = ({ attachment: a, messageId, onClose, onDeleted }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const openNew = () => {
    window.open(a.url, '_blank', 'noopener');
  };

  const download = () => {
    // Anchor with download attr triggers browser-native download. The
    // server marked Content-Disposition inline; on download we'll use
    // a query string the browser interprets — actually simpler: just
    // anchor click with download, which forces the save dialog.
    const link = document.createElement('a');
    link.href = a.url;
    link.download = a.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const remove = async () => {
    if (!confirm(`Delete "${a.name}"? This removes the file from storage and from the message.`)) return;
    setBusy(true); setError(null);
    try {
      await deleteChatFile({ key: a.key, messageId });
      onDeleted?.(a.key);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative',maxWidth:460,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>Attachment</div>

        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
          <MimeIcon mime={a.mime} size={32} tint="var(--fg)"/>
          <div style={{minWidth:0,flex:1}}>
            <div style={{fontWeight:600,fontSize:14,wordBreak:'break-word'}}>{a.name}</div>
            <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:3}}>{a.mime} · {fmtBytes(a.size)}</div>
          </div>
        </div>

        {error && <div style={{padding:10,marginBottom:14,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}

        <div style={{display:'flex',gap:8,justifyContent:'flex-end',flexWrap:'wrap'}}>
          <button className="btn-ghost" onClick={openNew} disabled={busy}>Open in new tab</button>
          <button className="btn-ghost" onClick={download} disabled={busy}>Download</button>
          <button className="btn-ghost" onClick={remove} disabled={busy} style={{color:'var(--support-red)'}}>
            {busy ? 'Deleting…' : 'Delete'}
          </button>
          <button className="btn-orange" onClick={onClose} disabled={busy}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ── message renderers ──────────────────────────────────────── */

/**
 * Live tool-step trace shown while a chat reply is being built.
 * Same vibe as claude.ai's step list: each tool call gets one row
 * showing skill name + input summary + a status dot + (when done)
 * the result summary and duration. The trace stays scrollable but
 * collapses out as soon as the message finishes streaming.
 */
/* Pull agent-run references out of an assistant reply. Spawn-tools
 * (spawn-slide-deck, future spawn-memo / spawn-proposal) emit
 * markdown with a `/runs/<uuid>/...` link in the body. We use that
 * link both as the user-visible CTA AND as the signal to mount an
 * inline trace. Returns `[{ runId, agentId }]` with a default
 * agentId per known viewer route. */
function extractAgentRunRefs(content) {
  if (!content) return [];
  const out = [];
  const seen = new Set();
  // Match (#|/)runs/<uuid>/<viewer-slug>[?agent=<agent-id>] — the
  // spawn-* tools emit the agent id as a query param so the inline
  // trace polls the right endpoint. Accept either the in-app hash
  // form (#runs/…) we now emit OR the legacy absolute path form
  // (/runs/…) so older replies in the chat history still mount the
  // inline trace.
  const RE = /[#/]runs\/([0-9a-f-]{36})(?:\/(\w+))?(?:\?agent=([a-z0-9-]+))?/gi;
  let m;
  while ((m = RE.exec(content)) != null) {
    const runId = m[1];
    if (seen.has(runId)) continue;
    seen.add(runId);
    const slug = (m[2] || '').toLowerCase();
    const agentId = m[3]
      || (slug === 'deck' ? 'agent-slide-deck-creator'
          : slug === 'memo' ? 'agent-proposal-builder'
          : null);
    if (agentId) out.push({ runId, agentId });
  }
  return out;
}

const ChatStepTrace = ({ steps }) => {
  if (!steps?.length) return null;
  return (
    <div style={{
      margin:'8px 0 4px',
      padding:'10px 12px',
      background:'var(--bg-alt)',
      border:'1px solid var(--border)',
      borderRadius:10,
      display:'flex',
      flexDirection:'column',
      gap:6,
      fontSize:12,
      lineHeight:1.4,
    }}>
      <div style={{
        fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',
        color:'var(--fg-muted)',fontWeight:600,marginBottom:2,
      }}>
        Live trace · {steps.length} step{steps.length === 1 ? '' : 's'}
      </div>
      {steps.map(s => <StepRow key={s.stepId} step={s}/>)}
    </div>
  );
};

const StepRow = ({ step }) => {
  if (step.phase === 'thinking') {
    return (
      <div style={{display:'flex',alignItems:'center',gap:8,color:'var(--fg-muted)',fontStyle:'italic'}}>
        <span className="live-dot" style={{flexShrink:0}}/>
        <span>Thinking…</span>
      </div>
    );
  }
  const running = step.phase === 'running';
  const ok = step.ok !== false;
  const dotColor = running ? '#FF6428' : (ok ? '#136127' : '#A20235');
  const dotChar = running ? '●' : (ok ? '✓' : '✕');
  return (
    <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
      <span style={{
        flexShrink:0,width:14,fontSize:11,fontWeight:700,
        color:dotColor,
      }}>
        {dotChar}
      </span>
      <div style={{minWidth:0,flex:1}}>
        <div style={{wordBreak:'break-word'}}>
          <span style={{fontWeight:600,color:'var(--fg)'}}>{step.skillName || step.skillId}</span>
          {step.summary && (
            <>
              <span style={{color:'var(--fg-muted)',margin:'0 6px'}}>·</span>
              <span style={{color:'var(--fg-muted)',fontSize:11.5}}>{step.summary}</span>
            </>
          )}
        </div>
        {!running && step.resultSummary && (
          <div style={{
            fontSize:11.5,color:ok ? 'var(--fg-muted)' : '#A20235',
            marginTop:2,wordBreak:'break-word',
          }}>
            <span style={{marginRight:4}}>←</span>
            {step.resultSummary}
            {Number.isFinite(step.durationMs) && (
              <>
                <span style={{margin:'0 4px',opacity:0.5}}>·</span>
                <span style={{fontFamily:'JetBrains Mono',fontSize:10.5,opacity:0.7}}>
                  {step.durationMs}ms
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* Empty-chat greeting. Time-of-day aware ("Good morning / afternoon /
   evening"), addresses the user by their first name. Sets the tone
   for a conversation rather than a blank prompt. Mirrors the morning-
   briefing voice. */

const ChatGreeting = ({ user, chatKind, peer }) => {
  // Direct-message thread with no messages yet — invite the user to write
  // the first line to their colleague rather than greeting them as Eve.
  if (chatKind === 'dm') {
    const peerName = (peer?.name || '').trim();
    return (
      <div className="chat-greeting">
        <h1 className="chat-greeting__hello">{peerName ? peerName : 'Direct message'}</h1>
        <p className="chat-greeting__ask">
          {peerName ? `Send ${peerName.split(/\s+/)[0]} a message — it lands in their chat history.` : 'Write the first message.'}
        </p>
      </div>
    );
  }
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || '';
  const hr = new Date().getHours();
  const partOfDay = hr < 5 ? 'evening' : hr < 12 ? 'morning' : hr < 18 ? 'afternoon' : 'evening';
  const greeting = firstName
    ? `Good ${partOfDay}, ${firstName}.`
    : `Good ${partOfDay}.`;
  const ask = chatKind === 'eve-nudge'
    ? 'What\'s on your mind today?'
    : 'How can I help you today?';
  return (
    <div className="chat-greeting">
      <h1 className="chat-greeting__hello">{greeting}</h1>
      <p className="chat-greeting__ask">{ask}</p>
    </div>
  );
};

/* Pull a deck-viewer reference out of a DM body. The deck-share message
 * the runtime posts (when a colleague shares a deck) carries the viewer
 * link on its own line (#runs/<uuid>/deck?agent=<id>); we lift it into an
 * "Open slide deck" button and strip the raw URL from the visible text.
 * Returns { text, deckUrl } — deckUrl is null for an ordinary message. */
function extractDeckRef(content) {
  if (!content) return { text: content, deckUrl: null };
  const RE = /[#/]runs\/([0-9a-f-]{36})\/deck(?:\?agent=([a-z0-9-]+))?/i;
  const m = RE.exec(content);
  if (!m) return { text: content, deckUrl: null };
  const agentId = m[2] || 'agent-slide-deck-creator';
  const deckUrl = `#runs/${m[1]}/deck?agent=${agentId}`;
  const text = content.replace(m[0], '').replace(/\n{2,}/g, '\n').trim();
  return { text, deckUrl };
}

/* A direct-message bubble. Own messages align right (orange-tinted), the
 * peer's align left with their avatar — the familiar two-sided chat layout.
 * Plain text only (no markdown / tool-steps / feedback): this is a
 * human-to-human note, not an AI turn. A deck-share DM additionally
 * renders an "Open slide deck" button (deckUrl) under the text. */
const DmMsg = ({ children, mine, authorName, authorImg, deckUrl }) => (
  <div style={{display:'flex',gap:12,alignItems:'flex-start',flexDirection: mine ? 'row-reverse' : 'row'}}>
    <Avatar src={authorImg} name={authorName}/>
    <div style={{flex:1,minWidth:0,maxWidth:680,display:'flex',flexDirection:'column',alignItems: mine ? 'flex-end' : 'flex-start'}}>
      <div style={{fontSize:11.5,color:'var(--fg-muted)',marginBottom:4,fontWeight:600}}>{authorName}</div>
      {children && (
        <div style={{
          fontSize:14.5,lineHeight:1.55,whiteSpace:'pre-wrap',overflowWrap:'anywhere',
          padding:'8px 12px',borderRadius:12,
          background: mine ? 'var(--orange)' : 'var(--bg-alt)',
          color: mine ? '#fff' : 'var(--fg)',
        }}>{children}</div>
      )}
      {deckUrl && (
        <a
          href={deckUrl}
          title="Open this slide deck in the viewer"
          style={{
            marginTop:6,display:'inline-flex',alignItems:'center',gap:7,
            padding:'7px 12px',borderRadius:10,textDecoration:'none',
            background:'var(--bg)',border:'1px solid var(--border)',
            color:'var(--orange)',fontSize:13,fontWeight:600,
          }}
        >
          <Icon name="file-presentation" size={15}/>
          Open slide deck
          <Icon name="chevron-right" size={14}/>
        </a>
      )}
    </div>
  </div>
);

const UserMsg = ({ children, user, attachments, messageId, onAttClick }) => (
  <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
    <Avatar src={user.img} name={user.name}/>
    <div style={{flex:1,minWidth:0,maxWidth:680}}>
      <div style={{fontSize:11.5,color:'var(--fg-muted)',marginBottom:4,fontWeight:600}}>{user.name}</div>
      {children && (
        <div style={{fontSize:14.5,lineHeight:1.55,whiteSpace:'pre-wrap',overflowWrap:'anywhere'}}>{children}</div>
      )}
      {attachments && attachments.length > 0 && (
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:children ? 6 : 0}}>
          {attachments.map(a => (
            <AttachmentChip key={a.key} a={a} onClick={() => onAttClick?.(a, messageId)}/>
          ))}
        </div>
      )}
    </div>
  </div>
);

const SelfLearningCard = ({ eval: ev }) => {
  if (ev.status === 'running') {
    return (
      <div style={{marginLeft:44,marginTop:-8,padding:'8px 12px',fontSize:12,color:'var(--fg-muted)',background:'var(--bg-alt)',borderRadius:8,display:'inline-flex',alignItems:'center',gap:8}}>
        <span className="live-dot"/>
        <span>🧠 Self-evaluating reply…</span>
      </div>
    );
  }
  if (ev.status === 'failed') {
    return (
      <div style={{marginLeft:44,marginTop:-8,padding:'8px 12px',fontSize:12,background:'#FCE6E0',color:'#A20235',borderRadius:8}}>
        Self-evaluation failed: {ev.error}
      </div>
    );
  }
  const r = ev.result || {};
  // Solved: subtle confirmation chip. Not solved: full card with suggestion.
  if (r.solved) {
    return (
      <div style={{marginLeft:44,marginTop:-8,padding:'4px 10px',fontSize:11.5,color:'var(--support-green)',background:'#DCF1E2',borderRadius:8,display:'inline-flex',alignItems:'center',gap:6,fontWeight:600}}>
        ✓ Self-check: solved ({r.confidence})
      </div>
    );
  }

  return (
    <div style={{marginLeft:44,marginTop:-8,padding:'12px 14px',background:'var(--bg-alt)',borderRadius:10,border:'1px solid #FFE7DC',maxWidth:680}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
        <span style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'#B83C0F',fontWeight:700}}>🧠 Self-learning</span>
        <span style={{fontSize:11,color:'var(--fg-muted)'}}>verdict: not fully solved · {r.confidence} confidence</span>
      </div>
      <div style={{fontSize:13,lineHeight:1.5,marginBottom:6}}>{r.reason}</div>
      {r.gap && (
        <div style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:10}}>
          <strong>Gap:</strong> {r.gap}
        </div>
      )}
      {r.suggestion && (
        <div style={{padding:10,background:'var(--bg)',borderRadius:8}}>
          <div style={{fontSize:10.5,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',fontWeight:600,marginBottom:4}}>
            Suggested {r.suggestion.kind}
          </div>
          <div style={{fontSize:13.5,fontWeight:600,marginBottom:4}}>{r.suggestion.title}</div>
          <div style={{fontSize:12.5,color:'var(--fg-muted)',lineHeight:1.45}}>{r.suggestion.description}</div>
          <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:6,fontStyle:'italic'}}>
            Track this manually via Agents → New agent if you want it acted on.
          </div>
        </div>
      )}
    </div>
  );
};

// Skills multi-select: a "+ Skills" chip in the composer toolbar that
// opens a popover with checkbox-per-skill. Whatever the user picks gets
// exposed as Anthropic tools on the next /api/chat call. Empty
// selection = backend defaults (the chart/table catalog).
const SkillsPicker = ({ catalog, selectedIds, onChange, open, setOpen, disabled, designSystem, setDesignSystem }) => {
  const wrapRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const searchRef = React.useRef(null);
  const [pos, setPos] = React.useState(null);
  const [query, setQuery] = React.useState('');

  // Compute viewport-aware position for the popover. Pinned to the
  // button's top edge; clamps left/right so the menu never falls off
  // screen on narrow viewports. Recomputes on resize / scroll while
  // the menu is open.
  const computePos = React.useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gutter = 12;
    const menuWidth = Math.min(380, vw - gutter * 2);
    let left = r.left;
    if (left + menuWidth > vw - gutter) left = vw - menuWidth - gutter;
    if (left < gutter) left = gutter;
    // Menu opens UPWARDS by default (composer is near bottom of viewport).
    // bottom offset = distance from bottom of viewport to top of button + 8px gap.
    const bottom = vh - r.top + 8;
    const maxHeight = Math.min(420, vh - bottom - gutter);
    setPos({ left, bottom, width: menuWidth, maxHeight });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    computePos();
    // Auto-focus the search on open so the user can just start typing.
    setTimeout(() => searchRef.current?.focus(), 0);
    const onDocClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onResize = () => computePos();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, setOpen, computePos]);

  // Clear search whenever the picker closes so the next open lands fresh.
  React.useEffect(() => { if (!open) setQuery(''); }, [open]);

  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter(x => x !== id));
    else onChange([...selectedIds, id]);
  };

  // Group by category so the list stays scannable as the catalog grows.
  // Filter against the search query (name + description, case-insensitive).
  const byCategory = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? catalog.filter(s =>
          (s.name || '').toLowerCase().includes(q) ||
          (s.description || '').toLowerCase().includes(q) ||
          (s.id || '').toLowerCase().includes(q))
      : catalog;
    const map = new Map();
    for (const s of filtered) {
      const c = s.category || 'Other';
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(s);
    }
    return Array.from(map.entries()).map(([cat, items]) => [cat, items.sort((a, b) => a.name.localeCompare(b.name))]);
  }, [catalog, query]);

  const count = selectedIds.length;
  return (
    <div ref={wrapRef} style={{position:'relative'}}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        title="Pick skills to expose as tools on the next reply. Empty = backend defaults (charts + tables)."
        className={`composer-chip${count > 0 ? ' composer-chip--active' : ''}`}
      >
        <span>Skills</span>
        {count > 0 && <span className="composer-chip__badge">{count}</span>}
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          className="composer-popover"
          style={{
            left:pos.left, bottom:pos.bottom, width:pos.width, maxHeight:pos.maxHeight,
          }}
        >
          <div className="composer-popover__head">
            <span className="composer-popover__title">Skills as tools</span>
            {count > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="composer-popover__clear"
              >Clear</button>
            )}
          </div>

          {/* Brand/house-style selector — Eraneos default. Choice is
              persisted per-user (DESIGN_LS_KEY in localStorage) so a
              user who switches to Eve stays there next session. */}
          {setDesignSystem && DESIGN_SYSTEMS.length > 1 && (
            <div className="composer-popover__brand">
              <div className="composer-popover__brand-label">House style</div>
              <div className="composer-popover__brand-tabs" role="tablist">
                {DESIGN_SYSTEMS.map(d => (
                  <button
                    key={d.id}
                    type="button"
                    role="tab"
                    aria-selected={designSystem === d.id}
                    className={`composer-popover__brand-tab${designSystem === d.id ? ' is-active' : ''}`}
                    onClick={() => setDesignSystem(d.id)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="composer-popover__search">
            <Icon name="search" size={14}/>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search skills…"
              className="composer-popover__search-input"
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
            />
            {query && (
              <button
                type="button"
                className="composer-popover__search-clear"
                onClick={() => { setQuery(''); searchRef.current?.focus(); }}
                aria-label="Clear search"
              >×</button>
            )}
          </div>
          <div className="composer-popover__body">
            {catalog.length === 0 && (
              <div style={{padding:'14px 18px',color:'var(--fg-muted)',fontSize:13}}>
                Loading skill catalog…
              </div>
            )}
            {catalog.length > 0 && byCategory.length === 0 && (
              <div style={{padding:'14px 18px',color:'var(--fg-muted)',fontSize:13}}>
                No skills match "{query}".
              </div>
            )}
            {byCategory.map(([cat, items]) => (
              <div key={cat} className="composer-popover__group">
                <div className="composer-popover__group-title">{cat}</div>
                {items.map(s => {
                  const on = selectedIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggle(s.id)}
                      className={`composer-popover__item${on ? ' composer-popover__item--on' : ''}`}
                    >
                      <span className={`composer-popover__tick${on ? ' composer-popover__tick--on' : ''}`}>
                        {on && <Icon name="check" size={11}/>}
                      </span>
                      <span className="composer-popover__item-text">
                        <span className="composer-popover__item-name">{s.name}</span>
                        <span className="composer-popover__item-desc">
                          {(s.description || '').slice(0, 140)}{(s.description || '').length > 140 ? '…' : ''}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Quick-action markers — Claude embeds them as `[[label]]` per the
// chat-action-defaults guideline. The chat surface strips them from
// the rendered text and renders them as orange chips that fire a
// follow-up message with the label as the user's content.
function extractActionChips(text) {
  if (!text) return { stripped: text, chips: [] };
  const chips = [];
  const seen = new Set();
  const stripped = text.replace(/\[\[([^\]\n]{1,40})\]\]/g, (_, label) => {
    const trimmed = label.trim();
    if (trimmed && !seen.has(trimmed.toLowerCase())) {
      seen.add(trimmed.toLowerCase());
      chips.push(trimmed);
    }
    return '';
  });
  return { stripped, chips };
}

const EveMsg = ({ content, streaming, steps, onAction, feedbackTarget = null, feedbackSnapshots = null, liveRunRefs = null, children }) => {
  // Backward-compat: <EveMsg>{string}</EveMsg> still works.
  const raw = content !== undefined ? content : (typeof children === 'string' ? children : '');
  const { stripped, chips } = extractActionChips(raw || '');
  // Trace visibility:
  //   - while streaming: ALWAYS show, sits directly under the "eOS · live"
  //     header so the user can watch each tool call land. This replaces the
  //     old generic "thinking…" placeholder.
  //   - after streaming: hidden by default, with a small bottom toggle the
  //     user can click to re-open the trace.
  const [tracePinned, setTracePinned] = React.useState(false);
  const hasSteps = Array.isArray(steps) && steps.length > 0;
  const showTrace = hasSteps && (streaming || tracePinned);
  const showToggle = !streaming && hasSteps;
  // Any agent run ids referenced in the reply (from spawn-* bridge
  // tools) — used to mount inline polling for the agent's step trace.
  // Merge refs parsed from the final prose with the ones surfaced live
  // on the spawn step (so the card mounts the instant the run starts,
  // mid-stream, and still resolves from text on a reloaded message).
  const runRefs = React.useMemo(() => {
    const fromText = extractAgentRunRefs(raw || '');
    const seen = new Set(fromText.map(r => r.runId));
    const merged = fromText.slice();
    for (const r of (liveRunRefs || [])) {
      if (r && r.runId && !seen.has(r.runId)) { seen.add(r.runId); merged.push(r); }
    }
    return merged;
  }, [raw, liveRunRefs]);

  return (
    <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
      <div style={{width:32,height:32,borderRadius:'50%',background:'var(--eve-gradient)',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
        <svg width="18" height="18" viewBox="0 0 209.67 209.67" xmlns="http://www.w3.org/2000/svg" aria-label="Eraneos">
          <path
            fill="#1A1414"
            d="M41.93 125.8 209.67 125.8 209.67 83.87C209.67 58.02 196.04 46.49 177.86 42.98 171.57 41.93 167.73 38.09 166.68 31.8 163.18 13.62 151.65-0.01 125.79-0.01L41.93-0.01 41.93 41.92 152.01 41.92C161.45 41.92 167.74 48.21 167.74 57.65L167.74 83.86 0 83.86 0 125.79C0 151.64 13.63 163.17 31.81 166.68 38.1 167.73 41.94 171.57 42.99 177.86 46.49 196.04 58.02 209.67 83.88 209.67L167.75 209.67 167.75 167.74 57.66 167.74C48.22 167.74 41.93 161.45 41.93 152.01L41.93 125.8Z"
          />
        </svg>
      </div>
      <div style={{flex:1,minWidth:0,maxWidth:760}}>
        <div style={{fontSize:11.5,color:'var(--fg-muted)',marginBottom:4,fontWeight:600}}>eOS · live</div>
        {showTrace && <ChatStepTrace steps={steps}/>}
        <div className="md-body" style={{fontSize:14.5,lineHeight:1.55}}>
          {typeof raw === 'string' ? <MarkdownView source={stripped}/> : children}
        </div>
        {/* Full agent-run viewer for any run the reply spawned (e.g.
            spawn-slide-deck / spawn-proposal-builder). Mounts the same
            RunBoard component used on /agents/<id>/runs/<runId> so the
            chat carries the complete step trace, artifact chips,
            human-approval form, viewer CTA, retry / stop controls etc.
            — no need to leave chat to follow the run. */}
        {runRefs.map(ref => (
          <div key={ref.runId} style={{marginTop:12}}>
            <AgentRunInline agentId={ref.agentId} runId={ref.runId}/>
          </div>
        ))}
        {chips.length > 0 && !streaming && (
          <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:8}}>
            {chips.map(label => (
              <button
                key={label}
                type="button"
                onClick={() => onAction?.(label)}
                style={{
                  padding:'5px 11px',fontSize:12,fontWeight:600,
                  background:'var(--orange)',color:'#fff',
                  border:0,borderRadius:14,cursor:'pointer',
                  letterSpacing:'0.01em',
                }}
                title={`Send "${label}" as a follow-up`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {feedbackTarget && (
          <div style={{marginTop:10}}>
            <ThumbsRow target={feedbackTarget} snapshots={feedbackSnapshots} compact/>
          </div>
        )}
        {showToggle && (
          <button
            type="button"
            onClick={() => setTracePinned(v => !v)}
            title={tracePinned ? 'Hide the per-tool trace for this turn' : 'Show every tool call this turn ran'}
            style={{
              marginTop:8,padding:'3px 8px',fontSize:10.5,
              background:'transparent',border:'1px solid var(--border)',
              borderRadius:6,color:'var(--fg-muted)',cursor:'pointer',
              display:'inline-flex',alignItems:'center',gap:5,
            }}
          >
            <span style={{fontFamily:'JetBrains Mono',fontSize:9}}>{tracePinned ? '▴' : '▾'}</span>
            {tracePinned ? 'Hide steps' : `Show ${steps.length} step${steps.length === 1 ? '' : 's'}`}
          </button>
        )}
      </div>
    </div>
  );
};
